import {
    getMediaDockQaUtf8ByteLength,
    incrementMediaDockQaCounter,
    mediaDockQaDiagnosticsEnabled,
    setMediaDockQaGauge,
} from 'lib/mediaDockQaDiagnostics'
import { mediaDockCinemaEnabled } from 'lib/mediaDockCinema'
import { applyMediaElementCaptions, readMediaElementCaptionsState } from 'lib/mediaCaptions'

const playbackStateByMediaKey = new Map()
const ownersById = new Map()
const listenersByMediaKey = new Map()

const MEDIA_DOCK_STORAGE_PREFIX = 'treechat:media-dock-history:v1'
const MEDIA_DOCK_SAVE_DEBOUNCE_MS = 1500
const MEDIA_DOCK_SERVER_SYNC_MIN_INTERVAL_MS = 10000
const PANEL_SCROLL_CONTAINER_SELECTOR = '.scroll-container'
const LEFT_SIDEBAR_SELECTOR = '#sidebar'

const tracksByMediaKey = new Map()
const trackOriginsByMediaKey = new Map()
const mediaDockListeners = new Set()
const dockOnReleaseOwnerIds = new Set()
const endedReleaseOwnerIds = new Set()
const pendingEndedReleaseOwnerIds = new Set()
const INLINE_MEDIA_HANDOFF_TTL_MS = 10000
const DOCK_MEDIA_HANDOFF_TTL_MS = 12000

let activeOwnerId = null
let ownerCounter = 0
let historyMediaKeys = []
let playlistMediaKeys = []
let nowPlayingMediaKey = null
let nowPlayingIsPlaying = false
let mediaDockHydrated = false
let mediaDockRestoringSnapshot = false
let mediaDockSaveTimeout = null
let mediaDockServerSyncInFlight = null
let mediaDockLastServerFetchAt = 0
let mediaDockPlaylistMutationQueue = Promise.resolve()
let mediaDockServerPlaylistRevision = 0
let mediaDockPlaylistUpdatedAt = 0
let mediaDockPendingRealtimeSync = null
let mediaDockServerPagination = {
    history: { hasMore: false, nextOffset: null, totalCount: 0 },
    playlist: { hasMore: false, nextOffset: null, totalCount: 0 },
}
const mediaDockServerPageLoads = {
    history: null,
    playlist: null,
}
let lastQaHistoryList = null
let lastQaPlaylistList = null
// The single in-flight ownership transition. A record exists only while a
// handoff is preparing; commit and cancel both clear it. See
// docs/media-handoff-state-machine.md.
let mediaHandoff = null
let mediaHandoffTokenCounter = 0
let mediaHandoffExpiryTimer = null
// Inline destinations cannot pass a token through the browser's later play
// event, so the coordinator remembers which token dispatched playback to an
// owner. A later explicit user play clears this implicit authorization.
const implicitMediaHandoffTokenByOwnerId = new Map()
// The winning destination keeps re-claiming after commit (YouTube state
// cycles), but only while no newer transition or owner has superseded it.
let lastCommittedMediaHandoff = null

function normalizeMediaKey(mediaKey) {
    return mediaKey ? String(mediaKey) : null
}

function getMediaDockGon() {
    return typeof globalThis !== 'undefined' ? globalThis.gon : null
}

function getOwner(ownerId) {
    if (!ownerId) return null
    return ownersById.get(ownerId) || null
}

function getSourceMediaHandoffEventFields(ownerId, mediaKey) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (
        !mediaHandoff ||
        mediaHandoff.sourceOwnerId !== ownerId ||
        mediaHandoff.mediaKey !== normalizedMediaKey
    ) {
        return {}
    }

    return {
        handoffToken: mediaHandoff.token,
        handoffDestination: mediaHandoff.destination,
    }
}

// An owner with no media element and no controller has no runtime behind it,
// so it is never playing. Without this it reads as "not paused", and its
// teardown looks like playback being torn away.
function ownerHasRuntime(owner) {
    return Boolean(owner && (owner.mediaEl || typeof owner.isPaused === 'function'))
}

function isOwnerPaused(owner) {
    if (!owner) return true
    if (!ownerHasRuntime(owner)) return true

    if (typeof owner.isPaused === 'function') {
        try {
            return owner.isPaused()
        } catch {
            return false
        }
    }

    return Boolean(owner.mediaEl?.paused)
}

function isOwnerConnected(owner) {
    return Boolean(owner?.mediaEl?.isConnected)
}

function pauseOwner(ownerId) {
    const owner = getOwner(ownerId)
    if (!owner || isOwnerPaused(owner)) return

    try {
        if (typeof owner.pause === 'function') {
            owner.pause()
        } else if (typeof owner.mediaEl?.pause === 'function') {
            owner.mediaEl.pause()
        }
    } catch {}
}

function pauseOtherPlayingOwnersForMediaKey(mediaKey, exceptOwnerId) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return

    ownersById.forEach((owner, ownerId) => {
        if (ownerId === exceptOwnerId || owner?.mediaKey !== normalizedMediaKey) return
        if (!isOwnerConnected(owner) || isOwnerPaused(owner) || isOwnerAtEnd(owner)) return

        pauseOwner(ownerId)
    })
}

function clearPendingEndedReleasesForMediaKey(mediaKey) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return

    pendingEndedReleaseOwnerIds.forEach(ownerId => {
        if (getOwner(ownerId)?.mediaKey === normalizedMediaKey) {
            pendingEndedReleaseOwnerIds.delete(ownerId)
        }
    })
}

function isOwnerInScope(owner, scopeElement) {
    if (!scopeElement) return true
    const mediaEl = owner?.mediaEl
    return Boolean(mediaEl && (mediaEl === scopeElement || scopeElement.contains(mediaEl)))
}

function normalizeRect(rect) {
    if (!rect) return null

    const left = Number(rect.left)
    const top = Number(rect.top)
    const width = Number.isFinite(Number(rect.width)) ? Number(rect.width) : null
    const height = Number.isFinite(Number(rect.height)) ? Number(rect.height) : null
    const right = Number.isFinite(Number(rect.right))
        ? Number(rect.right)
        : width == null
        ? null
        : left + width
    const bottom = Number.isFinite(Number(rect.bottom))
        ? Number(rect.bottom)
        : height == null
        ? null
        : top + height

    if (![left, top, right, bottom].every(Number.isFinite)) return null
    if (right <= left || bottom <= top) return null

    return {
        left,
        top,
        right,
        bottom,
        width: right - left,
        height: bottom - top,
    }
}

function getElementRect(element) {
    if (typeof element?.getBoundingClientRect !== 'function') return null
    incrementMediaDockQaCounter('layout.boundingClientRectReads')
    return normalizeRect(element.getBoundingClientRect())
}

function syncMediaDockQaRegistryGauges() {
    if (!mediaDockQaDiagnosticsEnabled()) return

    const normalizedMediaKey = normalizeMediaKey(nowPlayingMediaKey)
    let currentMediaOwners = 0
    const currentMediaResourceRuntimes = new Set()
    for (const owner of ownersById.values()) {
        if (owner.mediaKey !== normalizedMediaKey) continue
        currentMediaOwners += 1
        if (owner.mediaEl?.isConnected) currentMediaResourceRuntimes.add(owner.mediaEl)
    }

    let originElements = 0
    for (const origins of trackOriginsByMediaKey.values()) {
        originElements += origins.size
    }

    setMediaDockQaGauge('registry.playbackStates', playbackStateByMediaKey.size)
    setMediaDockQaGauge('registry.owners', ownersById.size)
    setMediaDockQaGauge('registry.tracks', tracksByMediaKey.size)
    setMediaDockQaGauge('registry.originMediaKeys', trackOriginsByMediaKey.size)
    setMediaDockQaGauge('registry.originElements', originElements)
    setMediaDockQaGauge('registry.history', historyMediaKeys.length)
    setMediaDockQaGauge('registry.playlist', playlistMediaKeys.length)
    setMediaDockQaGauge('currentMedia.owners', currentMediaOwners)
    setMediaDockQaGauge('currentMedia.resourceRuntimes', currentMediaResourceRuntimes.size)
}

function intersectRects(first, second) {
    if (!first || !second) return null

    const left = Math.max(first.left, second.left)
    const top = Math.max(first.top, second.top)
    const right = Math.min(first.right, second.right)
    const bottom = Math.min(first.bottom, second.bottom)

    if (right <= left || bottom <= top) return null
    return normalizeRect({ left, top, right, bottom })
}

function getViewportRect() {
    if (typeof window === 'undefined') return null

    const docEl = typeof document !== 'undefined' ? document.documentElement || document.body : null
    const width = window.innerWidth || docEl?.clientWidth || 0
    const height = window.innerHeight || docEl?.clientHeight || 0
    return normalizeRect({ left: 0, top: 0, right: width, bottom: height })
}

function getLeftSidebarOcclusionRect() {
    if (typeof document === 'undefined') return null

    const sidebar = document.querySelector?.(LEFT_SIDEBAR_SELECTOR)
    if (!sidebar || isElementHiddenByRenderedStyle(sidebar)) return null

    const rect = getElementRect(sidebar)
    const viewport = getViewportRect()
    if (!rect || !viewport) return null

    const visibleSidebarRect = intersectRects(rect, viewport)
    if (!visibleSidebarRect) return null

    return visibleSidebarRect
}

function applyOcclusionRect(bounds, sidebarRect) {
    if (!bounds || !sidebarRect) return bounds
    if (sidebarRect.right <= bounds.left || sidebarRect.left >= bounds.right) return bounds
    if (sidebarRect.bottom <= bounds.top || sidebarRect.top >= bounds.bottom) return bounds

    return normalizeRect({
        left: Math.max(bounds.left, sidebarRect.right),
        top: bounds.top,
        right: bounds.right,
        bottom: bounds.bottom,
    })
}

function applyLeftSidebarOcclusion(bounds) {
    return applyOcclusionRect(bounds, getLeftSidebarOcclusionRect())
}

function resolveVisibilityRoot(element, options = {}) {
    return (
        options.visibilityRoot ||
        (typeof element?.closest === 'function'
            ? element.closest(PANEL_SCROLL_CONTAINER_SELECTOR)
            : null)
    )
}

function getElementVisibilityBounds(element, options = {}) {
    let bounds = getViewportRect()
    if (!bounds) return null

    const root = resolveVisibilityRoot(element, options)
    if (root) {
        bounds = intersectRects(bounds, getElementRect(root))
    }

    return applyLeftSidebarOcclusion(applyPanelChromeOcclusion(bounds, root))
}

// fixed_header: the panel's header bar (while it shows) and the bottom editor
// stand over the top and foot of the scroll area. A post's media playing in
// place must pass under them like the rest of the post, so they trim what the
// user can see of it.
function applyPanelChromeOcclusion(bounds, root) {
    if (!bounds || typeof root?.closest !== 'function') return bounds
    const panel = root.closest('.panel-container-comp')
    if (!panel) return bounds
    let next = bounds
    const bar = panel.querySelector(
        '[data-testid="panel-place-bar"][data-banded="true"]:not([data-concealed="true"])',
    )
    const barRect = bar?.getBoundingClientRect?.()
    if (barRect?.height && barRect.bottom > next.top) {
        next = {
            left: next.left,
            right: next.right,
            bottom: next.bottom,
            top: Math.min(next.bottom, barRect.bottom),
        }
    }
    const host = panel.querySelector('[data-testid="panel-bottom-editor-host"]')
    const hostRect = host?.getBoundingClientRect?.()
    if (hostRect?.height && hostRect.top < next.bottom) {
        next = {
            left: next.left,
            right: next.right,
            top: next.top,
            bottom: Math.max(next.top, hostRect.top),
        }
    }
    return next === bounds ? bounds : normalizeRect(next)
}

function elementContainsFullscreenSurface(element, fullscreenSurface) {
    if (!element || !fullscreenSurface) return false
    return element === fullscreenSurface || element.contains?.(fullscreenSurface) === true
}

function getFullscreenPlaybackSurface(element, mediaKey = null) {
    if (typeof document !== 'undefined') {
        const fullscreenDocument =
            /** @type {Document & {webkitFullscreenElement?: Element | null}} */ (document)
        const fullscreenElement =
            fullscreenDocument.fullscreenElement || fullscreenDocument.webkitFullscreenElement
        if (elementContainsFullscreenSurface(element, fullscreenElement)) {
            return fullscreenElement
        }
    }

    if (element?.webkitDisplayingFullscreen === true) return element

    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (tracksByMediaKey.get(normalizedMediaKey)?.kind !== 'video') return null

    const videoElements = element?.querySelectorAll?.('video')
    for (let index = 0; index < (videoElements?.length || 0); index += 1) {
        if (videoElements[index]?.webkitDisplayingFullscreen === true) {
            return videoElements[index]
        }
    }

    return null
}

function isVideoOwner(owner) {
    if (typeof owner?.isVideo === 'function') {
        try {
            return owner.isVideo()
        } catch {
            return false
        }
    }

    return owner?.mediaEl?.tagName === 'VIDEO'
}

function shouldDeferDockPause(owner) {
    return tracksByMediaKey.get(owner?.mediaKey)?.kind === 'youtube'
}

// --- Handoff state machine ------------------------------------------------
// One record, one token counter. Begin is idempotent per mediaKey; commit and
// cancel clear the record; the TTL bounds preparation and falls back to a
// dock adoption when an abandoned source is still sounding offscreen.

function clearMediaHandoffExpiryTimer() {
    if (mediaHandoffExpiryTimer == null) return
    try {
        globalThis.clearTimeout(mediaHandoffExpiryTimer)
    } catch {}
    mediaHandoffExpiryTimer = null
}

function getMediaHandoff() {
    if (mediaHandoff && mediaHandoff.expiresAt <= Date.now()) {
        expireMediaHandoff(mediaHandoff.token)
    }
    return mediaHandoff
}

function clearMediaHandoff() {
    mediaHandoff = null
    clearMediaHandoffExpiryTimer()
    setMediaDockQaGauge('handoff.pending', 0)
}

function beginMediaHandoff({
    mediaKey,
    sourceOwnerId = null,
    destination,
    answerId = null,
    keepSourcePlaying = false,
    ttlMs = null,
}) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey || !destination) return null
    const normalizedAnswerId = answerId != null ? String(answerId) : null

    const existing = getMediaHandoff()
    if (existing && existing.mediaKey === normalizedMediaKey) {
        const isSameDestination =
            existing.destination === destination &&
            (destination !== 'inline' || existing.answerId === normalizedAnswerId)
        if (isSameDestination) {
            // Idempotent re-begin: a matching transition is already preparing.
            // Refresh source identity (a re-adopt after unmount passes null) but
            // never restart the record or mint a new token.
            if (sourceOwnerId && !existing.sourceOwnerId) {
                existing.sourceOwnerId = sourceOwnerId
            }
            return existing.token
        }
    }
    if (existing) {
        cancelMediaHandoff(existing.token, 'superseded')
    }

    // Beginning a new transition permanently retires any previously committed
    // token. Otherwise a delayed PLAYING event from the old winner could steal
    // ownership while this handoff is preparing or after it is cancelled.
    lastCommittedMediaHandoff = null
    mediaHandoffTokenCounter += 1
    const token = mediaHandoffTokenCounter
    const effectiveTtl =
        ttlMs ??
        (destination === 'inline' ? INLINE_MEDIA_HANDOFF_TTL_MS : DOCK_MEDIA_HANDOFF_TTL_MS)
    mediaHandoff = {
        token,
        mediaKey: normalizedMediaKey,
        sourceOwnerId: sourceOwnerId || null,
        destination,
        answerId: normalizedAnswerId,
        keepSourcePlaying: keepSourcePlaying === true,
        resumeIssued: false,
        expiresAt: Date.now() + effectiveTtl,
    }
    setMediaDockQaGauge('handoff.pending', 1)
    incrementMediaDockQaCounter('handoff.begins')

    clearMediaHandoffExpiryTimer()
    try {
        mediaHandoffExpiryTimer = globalThis.setTimeout(() => {
            mediaHandoffExpiryTimer = null
            expireMediaHandoff(token)
        }, effectiveTtl)
        // Node (tests): a pending safety timer must not hold the process open.
        mediaHandoffExpiryTimer?.unref?.()
    } catch {}

    return token
}

function commitMediaHandoff(token, ownerId, mediaKey) {
    if (!mediaHandoff || mediaHandoff.token !== token) return false
    lastCommittedMediaHandoff = {
        token,
        ownerId: String(ownerId),
        mediaKey: normalizeMediaKey(mediaKey),
    }
    clearMediaHandoff()
    incrementMediaDockQaCounter('handoff.commits')
    return true
}

function cancelMediaHandoff(token, reason = 'cancelled') {
    if (!mediaHandoff || mediaHandoff.token !== token) return false
    const cancelled = mediaHandoff
    clearMediaHandoff()
    incrementMediaDockQaCounter('handoff.cancels')
    emitMediaDockEvent({
        type: 'handoffCancelled',
        mediaKey: cancelled.mediaKey,
        handoffToken: cancelled.token,
        reason,
    })
    return true
}

// TTL expiry. An abandoned handoff whose source is still sounding offscreen
// falls back to a regular dock adoption so the audio gets a visible surface
// instead of playing on unattended.
function expireMediaHandoff(token) {
    if (!mediaHandoff || mediaHandoff.token !== token) return
    const expired = mediaHandoff
    clearMediaHandoff()
    incrementMediaDockQaCounter('handoff.expiries')

    const sourceOwner = getOwner(expired.sourceOwnerId)
    if (
        sourceOwner &&
        !isOwnerPaused(sourceOwner) &&
        !isOwnerAtEnd(sourceOwner) &&
        !isOwnerVisible(sourceOwner)
    ) {
        dockPlaybackOwner(expired.sourceOwnerId, { reason: 'handoff-timeout' })
        return
    }

    emitMediaDockEvent({
        type: 'handoffCancelled',
        mediaKey: expired.mediaKey,
        handoffToken: expired.token,
        reason: 'timeout',
    })
}

function isHandoffPendingForOwner(owner) {
    const handoff = getMediaHandoff()
    return Boolean(handoff && owner?.mediaKey && handoff.mediaKey === owner.mediaKey)
}

// Begin (or join) a dock-destination handoff. Returns the active token, or
// null when no coordination is needed. Used by MediaDock at adoption time so
// destination callbacks can be token-validated.
export function beginDockMediaHandoff(mediaKey, options = {}) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return null

    const existing = getMediaHandoff()
    const existingMatchesMedia = existing?.mediaKey === normalizedMediaKey
    if (existingMatchesMedia && existing.destination === 'dock') {
        if (options.sourceOwnerId && !existing.sourceOwnerId) {
            existing.sourceOwnerId = options.sourceOwnerId
        }
        return existing.token
    }
    if (!existingMatchesMedia && options.keepSourcePlaying !== true) {
        if (existing) {
            cancelMediaHandoff(existing.token, 'superseded')
        }
        return null
    }

    return beginMediaHandoff({
        mediaKey: normalizedMediaKey,
        sourceOwnerId:
            options.sourceOwnerId || (existingMatchesMedia ? existing.sourceOwnerId : null) || null,
        destination: 'dock',
        keepSourcePlaying:
            options.keepSourcePlaying === true ||
            (existingMatchesMedia && existing.keepSourcePlaying === true),
    })
}

export function getMediaHandoffInfo() {
    const handoff = getMediaHandoff()
    if (!handoff) return null
    return {
        token: handoff.token,
        mediaKey: handoff.mediaKey,
        sourceOwnerId: handoff.sourceOwnerId,
        destination: handoff.destination,
        keepSourcePlaying: handoff.keepSourcePlaying,
    }
}

// True while ownerId is the losing side of an in-flight handoff that keeps it
// sounding. Inline players use this to skip their offscreen self-pause and
// offscreen claim echoes during the transition window.
export function isMediaPlaybackHandoffSourceOwner(ownerId) {
    if (!ownerId) return false
    const handoff = getMediaHandoff()
    return Boolean(
        handoff && handoff.keepSourcePlaying && handoff.sourceOwnerId === String(ownerId),
    )
}

// Raw DOM pause sweeps (panel navigation safety nets) must not silence the
// element a keep-playing handoff is depending on.
export function isMediaElementPlaybackHandoffSource(mediaEl) {
    if (!mediaEl) return false
    const handoff = getMediaHandoff()
    if (!handoff?.keepSourcePlaying || !handoff.sourceOwnerId) return false

    const owner = getOwner(handoff.sourceOwnerId)
    return Boolean(owner?.mediaEl && owner.mediaEl === mediaEl)
}

// The dock calls this when a source origin has stably returned to view before
// the dock ever presented. Confirm the handoff's actual source owner is still
// mounted and visible; a stale origin can briefly outlive its owner during a
// panel teardown and must not cancel the destination's valid token.
export function cancelMediaHandoffForVisibleSource(mediaKey) {
    const handoff = getMediaHandoff()
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!handoff || !normalizedMediaKey || handoff.mediaKey !== normalizedMediaKey) return false
    if (handoff.destination !== 'dock' || !handoff.keepSourcePlaying) return false
    const sourceOwner = getOwner(handoff.sourceOwnerId)
    if (!sourceOwner || !isOwnerVisible(sourceOwner)) return false

    return cancelMediaHandoff(handoff.token, 'source-visible')
}

function safeOwnerCurrentTime(owner, fallback = 0) {
    if (!owner) return fallback

    if (typeof owner.getCurrentTime === 'function') {
        try {
            const currentTime = owner.getCurrentTime()
            if (Number.isFinite(currentTime) && currentTime >= 0) return currentTime
        } catch {}
    }

    return safeMediaCurrentTime(owner.mediaEl, fallback)
}

function safeOwnerDuration(owner, fallback = 0) {
    if (!owner) return fallback

    if (typeof owner.getDuration === 'function') {
        try {
            const duration = owner.getDuration()
            if (Number.isFinite(duration) && duration > 0) return duration
        } catch {}
    }

    return Number.isFinite(owner.mediaEl?.duration) && owner.mediaEl.duration > 0
        ? owner.mediaEl.duration
        : fallback
}

function isOwnerAtEnd(owner, { fallbackDuration = 0, endThreshold = 0.25 } = {}) {
    if (!owner) return false

    if (typeof owner.isEnded === 'function') {
        try {
            return Boolean(owner.isEnded())
        } catch {}
    }

    return isMediaElementAtEnd(owner.mediaEl, { fallbackDuration, endThreshold })
}

function reconcileNowPlayingIsPlaying() {
    if (!nowPlayingMediaKey) {
        nowPlayingIsPlaying = false
        return false
    }

    const activeOwner = getOwner(activeOwnerId)
    if (activeOwner?.mediaKey === nowPlayingMediaKey) {
        if (isOwnerConnected(activeOwner)) {
            if (!isOwnerPaused(activeOwner) && !isOwnerAtEnd(activeOwner)) {
                nowPlayingIsPlaying = true
                return true
            }
        } else {
            activeOwnerId = null
        }
    }

    const playingOwner = findPlayingOwnerForMediaKey(nowPlayingMediaKey)
    if (playingOwner) {
        activeOwnerId = playingOwner.ownerId
        nowPlayingIsPlaying = true
        return true
    }

    nowPlayingIsPlaying = false
    return false
}

function detachActiveOwnerFromPreviousMedia(ownerId, previousOwner) {
    if (!previousOwner?.mediaKey || activeOwnerId !== ownerId) return
    if (previousOwner.mediaKey !== nowPlayingMediaKey) return

    const wasPlaying = Boolean(!isOwnerPaused(previousOwner) && !isOwnerAtEnd(previousOwner))
    const handoffEventFields = getSourceMediaHandoffEventFields(ownerId, previousOwner.mediaKey)
    if (wasPlaying) {
        persistOwnerPlaybackState(previousOwner)
    }

    activeOwnerId = null
    nowPlayingIsPlaying = false

    if (wasPlaying) {
        emitMediaDockEvent({
            type: 'ownerLost',
            mediaKey: previousOwner.mediaKey,
            ownerId,
            ...handoffEventFields,
        })
    }
}

function isElementHiddenByRenderedStyle(element) {
    if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
        return false
    }

    const diagnosticsEnabled = mediaDockQaDiagnosticsEnabled()
    let current = element
    while (current) {
        if (diagnosticsEnabled) {
            incrementMediaDockQaCounter('layout.computedStyleReads')
        }
        const style = window.getComputedStyle(current)
        if (style) {
            if (style.display === 'none') return true
            if (style.visibility === 'hidden' || style.visibility === 'collapse') return true

            const opacity = Number.parseFloat(style.opacity)
            if (Number.isFinite(opacity) && opacity <= 0.01) return true
        }
        current = current.parentElement
    }

    return false
}

function seekOwnerToTime(owner, nextTime) {
    if (!owner || !Number.isFinite(nextTime) || nextTime < 0) return false

    try {
        if (typeof owner.seekTo === 'function') {
            owner.seekTo(nextTime)
            return true
        }
        if (owner.mediaEl) {
            owner.mediaEl.currentTime = nextTime
            return true
        }
    } catch {}

    return false
}

function setOwnerVolume(owner, volume) {
    if (!owner || !Number.isFinite(volume)) return
    const clamped = Math.max(0, Math.min(1, volume))

    try {
        if (typeof owner.setVolume === 'function') {
            owner.setVolume(clamped)
        } else if (owner.mediaEl) {
            owner.mediaEl.volume = clamped
        }
    } catch {}
}

function playOwner(owner) {
    if (!owner) return false

    try {
        if (typeof owner.play === 'function') {
            owner.play()?.catch?.(() => {})
            return true
        }
        if (typeof owner.mediaEl?.play === 'function') {
            owner.mediaEl.play()?.catch?.(() => {})
            return true
        }
    } catch {
        return false
    }

    return false
}

function getMediaOwnerVisibilityElement(owner) {
    const normalizedMediaKey = normalizeMediaKey(owner?.mediaKey)
    const origins = normalizedMediaKey ? trackOriginsByMediaKey.get(normalizedMediaKey) : null
    const mediaEl = owner?.mediaEl

    if (origins?.size) {
        let firstConnected = null

        for (const originEl of origins) {
            if (!originEl?.isConnected) continue
            if (!firstConnected) firstConnected = originEl
            if (!mediaEl) return originEl
            if (originEl === mediaEl || originEl.contains?.(mediaEl)) return originEl
        }

        if (!mediaEl && firstConnected) return firstConnected
    }

    return mediaEl || null
}

export function getActiveMediaPlaybackVisibilityElement(options = {}) {
    const owner = getOwner(activeOwnerId)
    if (!owner || isDockOwnerId(activeOwnerId)) return null
    if (isOwnerPaused(owner) || isOwnerAtEnd(owner)) return null
    if (!isOwnerInScope(owner, options.scopeElement || null)) return null

    const target = getMediaOwnerVisibilityElement(owner)
    return target?.isConnected ? target : null
}

function isOwnerVisible(owner, options = {}) {
    return isElementUserVisible(getMediaOwnerVisibilityElement(owner), {
        ...options,
        mediaKey: owner?.mediaKey,
    })
}

function dockPlaybackOwner(ownerId, { reason = 'offscreen' } = {}) {
    const owner = getOwner(ownerId)
    if (!owner?.mediaKey) return false
    if (isDockOwnerId(ownerId)) return false
    if (!tracksByMediaKey.has(owner.mediaKey)) return false
    // A handoff for this media is already preparing (this gesture's earlier
    // pause call, or a child-thread inline keep). The transition is owned by
    // that record — report handled so callers never fall through to a hard
    // pause of the sounding source.
    if (isHandoffPendingForOwner(owner)) return true
    if (isOwnerPaused(owner) || isOwnerAtEnd(owner)) return false

    persistOwnerPlaybackState(owner)
    dockOnReleaseOwnerIds.delete(ownerId)
    const deferOwnerPause = shouldDeferDockPause(owner)
    let handoffToken = null

    if (deferOwnerPause) {
        handoffToken = beginMediaHandoff({
            mediaKey: owner.mediaKey,
            sourceOwnerId: ownerId,
            destination: 'dock',
            keepSourcePlaying: true,
        })
    } else {
        if (activeOwnerId === ownerId) {
            activeOwnerId = null
        }
        if (nowPlayingMediaKey === owner.mediaKey) {
            nowPlayingIsPlaying = false
        }
        pauseOwner(ownerId)
    }

    emitMediaDockEvent({
        type: 'ownerOffscreen',
        mediaKey: owner.mediaKey,
        ownerId,
        reason,
        ...(deferOwnerPause ? { deferOwnerPause: true } : {}),
        ...(handoffToken != null ? getSourceMediaHandoffEventFields(ownerId, owner.mediaKey) : {}),
    })
    return true
}

function resetOwnerToStart(owner) {
    if (!owner) return

    try {
        if (typeof owner.resetToStart === 'function') {
            owner.resetToStart()
        } else if (owner.mediaEl) {
            owner.mediaEl.currentTime = 0
        }
    } catch {}
}

export function safeMediaCurrentTime(mediaEl, fallback = 0) {
    if (!mediaEl) return fallback
    return Number.isFinite(mediaEl.currentTime) && mediaEl.currentTime >= 0
        ? mediaEl.currentTime
        : fallback
}

function playbackStatesAreEqual(first, second) {
    if (first === second) return true
    if (!first || !second) return false

    const keys = new Set([...Object.keys(first), ...Object.keys(second)])
    for (const key of keys) {
        if (!Object.is(first[key], second[key])) return false
    }
    return true
}

export function isMediaElementAtEnd(mediaEl, { fallbackDuration = 0, endThreshold = 0.25 } = {}) {
    if (!mediaEl) return false

    const effectiveDuration = Number.isFinite(mediaEl.duration)
        ? mediaEl.duration
        : fallbackDuration

    if (!Number.isFinite(effectiveDuration) || effectiveDuration <= 0) {
        return Boolean(mediaEl.ended)
    }

    return (
        Boolean(mediaEl.ended) || safeMediaCurrentTime(mediaEl) >= effectiveDuration - endThreshold
    )
}

export function createMediaPlaybackOwnerId(prefix = 'media') {
    ownerCounter += 1
    return `${prefix}:${ownerCounter}`
}

export function syncMediaPlaybackOwner(ownerId, mediaKey, mediaEl, controller = {}) {
    if (!ownerId) return
    const playbackController = controller || {}
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    const previousOwner = ownersById.get(ownerId) || null
    if (previousOwner?.mediaKey !== normalizedMediaKey) {
        detachActiveOwnerFromPreviousMedia(ownerId, previousOwner)
        endedReleaseOwnerIds.delete(ownerId)
        pendingEndedReleaseOwnerIds.delete(ownerId)
        implicitMediaHandoffTokenByOwnerId.delete(ownerId)
        // A handoff source that re-targets to different media stops being a
        // usable source, but the destination's token must stay valid — same
        // as a source unmounting mid-handoff.
        if (mediaHandoff?.sourceOwnerId === ownerId) {
            mediaHandoff.sourceOwnerId = null
        }
    }

    ownersById.set(ownerId, {
        mediaKey: normalizedMediaKey,
        mediaEl: mediaEl || null,
        play: playbackController.play,
        pause: playbackController.pause,
        isPaused: playbackController.isPaused,
        isVideo: playbackController.isVideo,
        resetToStart: playbackController.resetToStart,
        seekTo: playbackController.seekTo,
        setVolume: playbackController.setVolume,
        getVolume: playbackController.getVolume,
        setPlaybackRate: playbackController.setPlaybackRate,
        getPlaybackRate: playbackController.getPlaybackRate,
        getAvailablePlaybackRates: playbackController.getAvailablePlaybackRates,
        getCurrentTime: playbackController.getCurrentTime,
        getDuration: playbackController.getDuration,
        isEnded: playbackController.isEnded,
        getCaptionsState: playbackController.getCaptionsState,
        setCaptionsEnabled: playbackController.setCaptionsEnabled,
    })
    setMediaDockQaGauge('registry.playbackStates', playbackStateByMediaKey.size)
}

export function unregisterMediaPlaybackOwner(ownerId) {
    if (!ownerId) return

    const owner = ownersById.get(ownerId) || null
    const wasPlaying = Boolean(owner && !isOwnerPaused(owner) && !isOwnerAtEnd(owner))
    const handoffEventFields = getSourceMediaHandoffEventFields(ownerId, owner?.mediaKey)

    if (activeOwnerId === ownerId) {
        activeOwnerId = null
    }

    ownersById.delete(ownerId)
    dockOnReleaseOwnerIds.delete(ownerId)
    endedReleaseOwnerIds.delete(ownerId)
    pendingEndedReleaseOwnerIds.delete(ownerId)
    implicitMediaHandoffTokenByOwnerId.delete(ownerId)
    // A source unmounting mid-handoff cannot keep sounding; the record stays
    // so the destination's token remains valid and its claim still commits.
    if (mediaHandoff?.sourceOwnerId === ownerId) {
        mediaHandoff.sourceOwnerId = null
    }
    setMediaDockQaGauge('registry.playbackStates', playbackStateByMediaKey.size)

    // A playing element unmounted (e.g. its post left the screen during
    // navigation). Persist where it got to and tell the media dock so it can
    // adopt playback and keep going.
    if (owner?.mediaKey && owner.mediaKey === nowPlayingMediaKey && wasPlaying) {
        persistOwnerPlaybackState(owner)
        nowPlayingIsPlaying = false
        emitMediaDockEvent({
            type: 'ownerLost',
            mediaKey: owner.mediaKey,
            ownerId,
            ...handoffEventFields,
        })
    }
}

const DOCK_OWNER_PREFIX = 'media-dock:'

const isDockOwnerId = ownerId => String(ownerId || '').startsWith(DOCK_OWNER_PREFIX)

function isPlaybackGatedInlineHandoffFromOwner(handoff, token, sourceOwnerId, mediaKey) {
    return Boolean(
        handoff &&
            token != null &&
            handoff.token === token &&
            handoff.destination === 'inline' &&
            handoff.keepSourcePlaying &&
            handoff.sourceOwnerId === String(sourceOwnerId) &&
            handoff.mediaKey === normalizeMediaKey(mediaKey),
    )
}

// Playing through ANY owner — an inline player on screen or the dock. Cards
// with their own play button mirror this rather than dock ownership alone.
export function isMediaKeyPlaying(mediaKey) {
    return Boolean(findPlayingOwnerForMediaKey(mediaKey))
}

// Presentation outlives playback: a paused or completed runtime still occupies
// the dock until it is closed, replaced, or returned to the thread.
let mediaDockPresentedMediaKey = null

export function setMediaDockPresentedMediaKey(mediaKey) {
    const next = normalizeMediaKey(mediaKey)
    if (next === mediaDockPresentedMediaKey) return
    mediaDockPresentedMediaKey = next
    emitMediaDockEvent({ type: 'presentation', mediaKey: next })
}

export function isMediaKeyPresentedInDock(mediaKey) {
    const key = normalizeMediaKey(mediaKey)
    return Boolean(key && (key === mediaDockPresentedMediaKey || isMediaKeyPlayingInDock(key)))
}

export function isMediaKeyPlayingInDock(mediaKey) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return false

    const activeOwner = getOwner(activeOwnerId)
    if (
        activeOwnerId &&
        isDockOwnerId(activeOwnerId) &&
        activeOwner?.mediaKey === normalizedMediaKey &&
        isOwnerConnected(activeOwner) &&
        !isOwnerPaused(activeOwner) &&
        !isOwnerAtEnd(activeOwner)
    ) {
        return true
    }

    const playingOwner = findPlayingOwnerForMediaKey(normalizedMediaKey, {
        ownerIdPredicate: isDockOwnerId,
    })
    if (!playingOwner || !isDockOwnerId(playingOwner.ownerId)) return false

    activeOwnerId = playingOwner.ownerId
    if (nowPlayingMediaKey === normalizedMediaKey) {
        nowPlayingIsPlaying = true
    }
    return true
}

function releaseActiveDockOwnerForTransfer() {
    if (!activeOwnerId || !isDockOwnerId(activeOwnerId)) return false

    const dockOwnerId = activeOwnerId
    const dockOwner = getOwner(dockOwnerId)
    pauseOwner(dockOwnerId)
    if (dockOwner?.mediaKey) {
        persistOwnerPlaybackState(dockOwner)
    }
    releaseMediaPlayback(dockOwnerId)
    return true
}

export function transferMediaPlaybackControlFromDock(ownerId) {
    const owner = getOwner(ownerId)
    if (!owner?.mediaKey) return false
    if (!activeOwnerId || activeOwnerId === ownerId || !isDockOwnerId(activeOwnerId)) return false

    return releaseActiveDockOwnerForTransfer()
}

function claimDockTrack(mediaKey, ownerId) {
    historyMediaKeys = [mediaKey, ...historyMediaKeys.filter(key => key !== mediaKey)]
    queueMediaDockHistoryAdd(mediaKey)
    nowPlayingMediaKey = mediaKey
    nowPlayingIsPlaying = true
    emitMediaDockEvent({ type: 'claim', mediaKey, ownerId })
}

export function claimMediaPlayback(ownerId, options = {}) {
    const nextOwner = getOwner(ownerId)
    if (!nextOwner?.mediaKey) return false
    const { transferFromDock = false, handoffToken: explicitHandoffToken = null } = options
    const handoff = getMediaHandoff()
    const implicitHandoffToken = implicitMediaHandoffTokenByOwnerId.get(ownerId) ?? null
    const handoffToken = explicitHandoffToken ?? implicitHandoffToken
    const usesImplicitHandoffToken = explicitHandoffToken == null && implicitHandoffToken != null
    const matchesCurrentHandoff =
        handoffToken != null && handoff != null && handoffToken === handoff.token
    const matchesCommittedHandoff =
        handoffToken != null &&
        handoff == null &&
        lastCommittedMediaHandoff?.token === handoffToken &&
        lastCommittedMediaHandoff.ownerId === String(ownerId) &&
        lastCommittedMediaHandoff.mediaKey === nextOwner.mediaKey

    // A destination callback carrying a stale token belongs to a superseded
    // or cancelled handoff — an obsolete iframe must never reclaim ownership.
    // A committed token is honored only for the winning owner/media pair and
    // only while no newer handoff is preparing.
    if (handoffToken != null && !matchesCurrentHandoff && !matchesCommittedHandoff) {
        if (usesImplicitHandoffToken) {
            implicitMediaHandoffTokenByOwnerId.delete(ownerId)
        }
        // The stale callback usually means the obsolete iframe has just
        // reached PLAYING. Refusing ownership is not enough: silence that
        // runtime so it cannot produce ghost audio beside the real winner.
        pauseOwner(ownerId)
        return false
    }

    // Once inline resume has selected and tagged a destination, another
    // mounted copy of the same media must not commit merely because it also
    // reaches PLAYING. Only the source echo or the token-bearing destination
    // is authorized during that window.
    if (
        handoff?.destination === 'inline' &&
        handoff.resumeIssued &&
        handoff.mediaKey === nextOwner.mediaKey &&
        handoff.sourceOwnerId !== ownerId &&
        handoffToken == null
    ) {
        pauseOwner(ownerId)
        return false
    }

    // While the dock is playing, inline players never take over: the claimant
    // is paused immediately (inside its own play handler, so it barely
    // starts) and the dock is told to adopt the track instead. Explicit user
    // commands can opt into transferring ownership back to the inline player.
    if (
        activeOwnerId &&
        activeOwnerId !== ownerId &&
        isDockOwnerId(activeOwnerId) &&
        !isDockOwnerId(ownerId) &&
        !isOwnerPaused(getOwner(activeOwnerId))
    ) {
        if (transferFromDock) {
            // A tokenized reverse handoff is not complete merely because an
            // asynchronous player accepted its play command. Keep the dock
            // sounding until the inline destination reports PLAYING and this
            // claim commits the handoff below.
            if (
                !isPlaybackGatedInlineHandoffFromOwner(
                    handoff,
                    handoffToken,
                    activeOwnerId,
                    nextOwner.mediaKey,
                )
            ) {
                releaseActiveDockOwnerForTransfer()
            }
        } else {
            pauseOwner(ownerId)
            if (tracksByMediaKey.has(nextOwner.mediaKey)) {
                emitMediaDockEvent({
                    type: 'claimDeflected',
                    mediaKey: nextOwner.mediaKey,
                    ownerId,
                })
            }
            return false
        }
    }

    // Handoff resolution. The source keeps playing while its handoff prepares,
    // so its own claim echoes are benign — they neither cancel nor commit. A
    // claim for different media cancels (the user moved on); any other claim
    // for the handoff media is the destination arriving: commit.
    let committedCurrentHandoff = false
    if (
        handoff &&
        !(handoff.sourceOwnerId === ownerId && handoff.mediaKey === nextOwner.mediaKey)
    ) {
        if (nextOwner.mediaKey !== handoff.mediaKey) {
            cancelMediaHandoff(handoff.token, 'superseded')
        } else {
            committedCurrentHandoff = commitMediaHandoff(handoff.token, ownerId, nextOwner.mediaKey)
        }
    }

    if (!committedCurrentHandoff && !matchesCommittedHandoff) {
        lastCommittedMediaHandoff = null
    }

    if (activeOwnerId && activeOwnerId !== ownerId) {
        pauseOwner(activeOwnerId)
    }

    activeOwnerId = ownerId
    endedReleaseOwnerIds.delete(ownerId)
    clearPendingEndedReleasesForMediaKey(nextOwner.mediaKey)

    if (tracksByMediaKey.has(nextOwner.mediaKey)) {
        claimDockTrack(nextOwner.mediaKey, ownerId)
    }

    return true
}

export function releaseMediaPlayback(ownerId, options = {}) {
    const owner = getOwner(ownerId)
    const releaseMediaKey = normalizeMediaKey(options.mediaKey)
    if (releaseMediaKey && owner?.mediaKey !== releaseMediaKey) return

    const dockOnRelease = dockOnReleaseOwnerIds.has(ownerId)
    const ended = options.ended === true
    dockOnReleaseOwnerIds.delete(ownerId)
    if (ended && endedReleaseOwnerIds.has(ownerId)) return

    // A visible paused source is user intent, but mobile browsers can also
    // pause a YouTube iframe after its panel moves offscreen. Keep that dock
    // transition alive so the hidden destination can finish loading and take
    // over. Ended media and inline handoffs retain their existing semantics.
    const handoff = getMediaHandoff()
    const sourcePaused = isOwnerPaused(owner)
    const shouldCancelPausedSource =
        sourcePaused && (handoff?.destination !== 'dock' || isOwnerVisible(owner))
    if (handoff && handoff.sourceOwnerId === ownerId && (ended || shouldCancelPausedSource)) {
        cancelMediaHandoff(handoff.token, ended ? 'source-ended' : 'source-paused')
    }

    const releaseMatchesNowPlaying = owner?.mediaKey && owner.mediaKey === nowPlayingMediaKey
    const releasedActiveOwner = activeOwnerId === ownerId
    const preferredPlayingOwner = releaseMatchesNowPlaying
        ? findPlayingOwnerForMediaKey(owner.mediaKey, {
              exceptOwnerId: ownerId,
              ownerIdPredicate: candidateOwnerId =>
                  candidateOwnerId === activeOwnerId || isDockOwnerId(candidateOwnerId),
          })
        : null
    const otherPlayingOwner =
        preferredPlayingOwner ||
        (releaseMatchesNowPlaying
            ? findPlayingOwnerForMediaKey(owner.mediaKey, { exceptOwnerId: ownerId })
            : null)
    const endedActivePlayback =
        ended &&
        releaseMatchesNowPlaying &&
        (releasedActiveOwner || pendingEndedReleaseOwnerIds.has(ownerId))

    if (endedActivePlayback) {
        pendingEndedReleaseOwnerIds.delete(ownerId)
        activeOwnerId = null
        nowPlayingIsPlaying = false
        pauseOtherPlayingOwnersForMediaKey(owner.mediaKey, ownerId)
        emitMediaDockEvent({
            type: 'release',
            mediaKey: owner.mediaKey,
            ownerId,
            ended: true,
            dockOnRelease,
        })
        endedReleaseOwnerIds.add(ownerId)
        return
    }

    if (otherPlayingOwner) {
        if (releasedActiveOwner && !ended) {
            // Native media commonly emits pause immediately before ended. If
            // another mounted copy is still reporting playback, remember the
            // authoritative owner so its following explicit ended release can
            // advance the playlist instead of being swallowed by the copy.
            pendingEndedReleaseOwnerIds.add(ownerId)
        }
        if (releasedActiveOwner || !activeOwnerId) {
            activeOwnerId = otherPlayingOwner.ownerId
        }
        nowPlayingIsPlaying = true

        // The media is still sounding, so this is not a stop — but if the owner
        // that just let go was the DOCK, the dock has to hear about it. It
        // keeps its own takeover state, and while tuned into the radio it
        // re-adopts the live track on every tick; left uninformed it drags
        // playback back out of the inline player the listener just moved it to.
        if (isDockOwnerId(ownerId) && !isDockOwnerId(otherPlayingOwner.ownerId)) {
            emitMediaDockEvent({
                type: 'release',
                mediaKey: owner.mediaKey,
                ownerId,
                ended: false,
                dockOnRelease: false,
                takenOverByOwnerId: otherPlayingOwner.ownerId,
            })
        }
        return
    }

    if (activeOwnerId === ownerId) {
        activeOwnerId = null
    }

    if (
        owner?.mediaKey &&
        owner.mediaKey === nowPlayingMediaKey &&
        (nowPlayingIsPlaying || ended) &&
        !findPlayingOwnerForMediaKey(owner.mediaKey)
    ) {
        nowPlayingIsPlaying = false
        emitMediaDockEvent({
            type: 'release',
            mediaKey: owner.mediaKey,
            ownerId,
            ended,
            dockOnRelease,
        })
        if (ended) {
            pendingEndedReleaseOwnerIds.delete(ownerId)
            endedReleaseOwnerIds.add(ownerId)
        }
    }
}

export function stopActiveMediaPlayback(options = {}) {
    const { resetVideoToStart = false, scopeElement = null, dockOnRelease = false } = options
    if (!activeOwnerId) {
        // A source can unmount while its destination is still preparing. A
        // global hard stop (story/modal open) must still retire that pending
        // destination even though teardown already cleared activeOwnerId.
        if (!dockOnRelease && !scopeElement) {
            const handoff = getMediaHandoff()
            if (handoff) {
                cancelMediaHandoff(handoff.token, 'stopped')
            }
        }
        return
    }

    const owner = getOwner(activeOwnerId)
    if (!owner) {
        activeOwnerId = null
        return
    }

    if (!isOwnerInScope(owner, scopeElement)) return

    // One navigation gesture can pause the same panel several times (openRight
    // plus the slide's index change plus panel hide effects). Once a handoff
    // for this media is preparing, later dock-on-release stop calls are echoes
    // of the same gesture — the transition is already owned; never fall
    // through to a hard pause of the still-sounding source. A stop WITHOUT
    // dockOnRelease is a genuine "silence everything" command (e.g. the story
    // modal opening): it cancels the transition and pauses.
    if (isHandoffPendingForOwner(owner)) {
        if (dockOnRelease) return
        const handoff = getMediaHandoff()
        if (handoff) {
            cancelMediaHandoff(handoff.token, 'stopped')
        }
    }

    const { mediaKey } = owner
    const shouldResetToStart = resetVideoToStart && isVideoOwner(owner)

    if (
        dockOnRelease &&
        !shouldResetToStart &&
        owner.mediaKey === nowPlayingMediaKey &&
        nowPlayingIsPlaying &&
        !isOwnerAtEnd(owner) &&
        dockPlaybackOwner(activeOwnerId, { reason: 'dock-on-release' })
    ) {
        return
    }

    if (
        dockOnRelease &&
        owner.mediaKey === nowPlayingMediaKey &&
        nowPlayingIsPlaying &&
        !isOwnerAtEnd(owner) &&
        !isOwnerPaused(owner)
    ) {
        // Mark for dock adoption only when this call actually pauses something
        // — a transiently unreadable player state must not leave a stale
        // marker that converts a later genuine user pause into a dock launch.
        dockOnReleaseOwnerIds.add(activeOwnerId)
    }

    pauseOwner(activeOwnerId)

    if (shouldResetToStart) {
        resetOwnerToStart(owner)

        if (mediaKey) {
            setMediaPlaybackState(mediaKey, { currentTime: 0 })
        }
    }

    activeOwnerId = null
}

export function isInlineMediaPlaybackHandoffPending(mediaKey = null) {
    const handoff = getMediaHandoff()
    if (!handoff || handoff.destination !== 'inline') return false
    return !mediaKey || handoff.mediaKey === normalizeMediaKey(mediaKey)
}

export function isInlineMediaPlaybackHandoffPendingForAnswer(answerId, mediaKey = null) {
    const handoff = getMediaHandoff()
    if (!handoff || handoff.destination !== 'inline') return false
    if (!answerId || String(handoff.answerId) !== String(answerId)) return false

    return !mediaKey || handoff.mediaKey === normalizeMediaKey(mediaKey)
}

export function prepareActiveMediaPlaybackForInlineHandoff(answerId, options = {}) {
    const normalizedAnswerId = answerId ? String(answerId) : null
    const ownerId = activeOwnerId
    const owner = getOwner(ownerId)
    const track = owner?.mediaKey ? tracksByMediaKey.get(owner.mediaKey) : null

    if (!normalizedAnswerId || !ownerId || !owner?.mediaKey || !track) return null
    if (String(track.answerId || '') !== normalizedAnswerId) return null
    if (!isOwnerInScope(owner, options.scopeElement || null)) return null
    if (isOwnerPaused(owner) || isOwnerAtEnd(owner)) return null

    persistOwnerPlaybackState(owner)
    const handoffToken = beginMediaHandoff({
        mediaKey: owner.mediaKey,
        sourceOwnerId: ownerId,
        destination: 'inline',
        answerId: normalizedAnswerId,
        keepSourcePlaying: true,
        ttlMs: Number.isFinite(options.ttlMs) && options.ttlMs > 0 ? options.ttlMs : null,
    })
    if (handoffToken == null) return null

    // The source keeps playing through the slide and the destination iframe's
    // boot; the child panel's player pauses it by claiming (commit) once it is
    // actually producing playback.
    emitMediaDockEvent({
        type: 'inlineHandoffPending',
        mediaKey: owner.mediaKey,
        ownerId,
        answerId: normalizedAnswerId,
        handoffToken,
    })

    return owner.mediaKey
}

export function resumePendingInlineMediaPlayback(mediaKey, options = {}) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    const handoff = getMediaHandoff()
    if (
        !handoff ||
        handoff.destination !== 'inline' ||
        !normalizedMediaKey ||
        handoff.mediaKey !== normalizedMediaKey
    ) {
        return false
    }
    // Play is dispatched once; the destination's own claim commits. Reporting
    // true stops caller retry loops without treating a dispatched play as a
    // finished handoff.
    if (handoff.resumeIssued) return true

    let targetOwnerId = null
    for (const [ownerId, owner] of ownersById) {
        if (ownerId === handoff.sourceOwnerId) continue
        if (owner?.mediaKey !== normalizedMediaKey || isDockOwnerId(ownerId)) continue
        if (!isOwnerConnected(owner) || !isOwnerInScope(owner, options.scopeElement || null)) {
            continue
        }
        targetOwnerId = ownerId
        break
    }
    if (!targetOwnerId) return false

    // Re-persist the live source position so the destination resumes where the
    // still-playing source actually is, not where it was at click time.
    const sourceOwner = getOwner(handoff.sourceOwnerId)
    if (sourceOwner && !isOwnerPaused(sourceOwner)) {
        persistOwnerPlaybackState(sourceOwner)
    }

    if (
        !playMediaPlaybackOwner(targetOwnerId, {
            transferFromDock: true,
            handoffToken: handoff.token,
        })
    ) {
        return false
    }

    handoff.resumeIssued = true
    return true
}

export function dockActiveMediaPlaybackIfOutsideVisibleArea(options = {}) {
    const { scopeElement = null, visibilityRoot = null } = options
    if (!activeOwnerId) return false

    const ownerId = activeOwnerId
    const owner = getOwner(ownerId)
    if (!owner) {
        activeOwnerId = null
        return false
    }

    if (!isOwnerInScope(owner, scopeElement)) return false
    if (isOwnerVisible(owner, { visibilityRoot })) return false

    return dockPlaybackOwner(ownerId, { reason: 'visibility' })
}

function emitMediaPlaybackState(mediaKey) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return

    const listeners = listenersByMediaKey.get(normalizedMediaKey)
    if (!listeners?.size) return

    const nextState = playbackStateByMediaKey.get(normalizedMediaKey) || null
    listeners.forEach(listener => {
        try {
            listener(nextState)
        } catch {}
    })
}

export function getMediaPlaybackState(mediaKey) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return null
    return playbackStateByMediaKey.get(normalizedMediaKey) || null
}

export function isMediaPlaybackLoaded(mediaKey) {
    return Boolean(getMediaPlaybackState(mediaKey)?.isLoaded)
}

export function setMediaPlaybackState(mediaKey, nextState) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey || !nextState) return null

    const previousSnapshot = playbackStateByMediaKey.get(normalizedMediaKey) || null
    const snapshot = {
        ...(previousSnapshot || {}),
        ...nextState,
    }

    if (previousSnapshot && playbackStatesAreEqual(previousSnapshot, snapshot)) {
        return previousSnapshot
    }

    playbackStateByMediaKey.set(normalizedMediaKey, snapshot)
    syncMediaDockQaRegistryGauges()
    emitMediaPlaybackState(normalizedMediaKey)
    if (
        historyMediaKeys.includes(normalizedMediaKey) ||
        playlistMediaKeys.includes(normalizedMediaKey)
    ) {
        scheduleMediaDockHistorySave()
    }
    return snapshot
}

export function markMediaPlaybackLoaded(mediaKey) {
    return setMediaPlaybackState(mediaKey, { isLoaded: true })
}

export function persistMediaElementPlaybackState(
    mediaKey,
    mediaEl,
    overrides = {},
    { fallbackDuration = 0, endThreshold = 0.25, skipAtEnd = false } = {},
) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return null

    const previousSnapshot = getMediaPlaybackState(normalizedMediaKey)
    if (
        overrides.currentTime == null &&
        skipAtEnd &&
        isMediaElementAtEnd(mediaEl, { fallbackDuration, endThreshold })
    ) {
        return previousSnapshot
    }

    const measuredCurrentTime = safeMediaCurrentTime(mediaEl, previousSnapshot?.currentTime ?? 0)
    const shouldPreservePreviousTime =
        overrides.currentTime == null &&
        previousSnapshot?.currentTime > 0.05 &&
        measuredCurrentTime <= 0.05 &&
        !mediaEl?.ended

    return setMediaPlaybackState(normalizedMediaKey, {
        ...overrides,
        currentTime:
            overrides.currentTime ??
            (shouldPreservePreviousTime ? previousSnapshot.currentTime : measuredCurrentTime),
    })
}

function persistOwnerPlaybackState(
    owner,
    overrides = {},
    { fallbackDuration = 0, endThreshold = 0.25, skipAtEnd = false } = {},
) {
    const normalizedMediaKey = normalizeMediaKey(owner?.mediaKey)
    if (!normalizedMediaKey) return null

    const previousSnapshot = getMediaPlaybackState(normalizedMediaKey)
    if (
        overrides.currentTime == null &&
        skipAtEnd &&
        isOwnerAtEnd(owner, { fallbackDuration, endThreshold })
    ) {
        return previousSnapshot
    }

    const measuredCurrentTime = safeOwnerCurrentTime(owner, previousSnapshot?.currentTime ?? 0)
    const shouldPreservePreviousTime =
        overrides.currentTime == null &&
        previousSnapshot?.currentTime > 0.05 &&
        measuredCurrentTime <= 0.05 &&
        !isOwnerAtEnd(owner, { fallbackDuration, endThreshold })
    const duration = safeOwnerDuration(owner, previousSnapshot?.duration ?? fallbackDuration)

    return setMediaPlaybackState(normalizedMediaKey, {
        ...(duration > 0 && overrides.duration == null ? { duration } : {}),
        ...overrides,
        currentTime:
            overrides.currentTime ??
            (shouldPreservePreviousTime ? previousSnapshot.currentTime : measuredCurrentTime),
    })
}

export function getMediaOwnerPlaybackSnapshot(owner, previous = {}) {
    if (!owner) {
        return {
            currentTime: Number.isFinite(previous?.currentTime) ? previous.currentTime : 0,
            duration: Number.isFinite(previous?.duration) ? previous.duration : 0,
        }
    }

    return {
        currentTime: safeOwnerCurrentTime(
            owner,
            Number.isFinite(previous?.currentTime) ? previous.currentTime : 0,
        ),
        duration: safeOwnerDuration(
            owner,
            Number.isFinite(previous?.duration) ? previous.duration : 0,
        ),
    }
}

export function clearMediaPlaybackState(mediaKey) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return
    playbackStateByMediaKey.delete(normalizedMediaKey)
    syncMediaDockQaRegistryGauges()
    emitMediaPlaybackState(normalizedMediaKey)
}

// ---------------------------------------------------------------------------
// Media dock: track registry, playlist history, and now-playing subscription.
//
// Inline players (audio attachments, answer videos) register a "track" for
// their mediaKey. Whenever a registered track claims playback it is pushed to
// the front of the playlist history and becomes the dock's now-playing item.
// Tracks without registered metadata (e.g. TTS audio) never reach the dock.
// ---------------------------------------------------------------------------

function emitMediaDockEvent(event) {
    syncMediaDockQaRegistryGauges()
    mediaDockListeners.forEach(listener => {
        try {
            listener(event)
        } catch {}
    })
    scheduleMediaDockHistorySave()
}

// --- Persistence -----------------------------------------------------------
// History stays local to the browser for immediate boot and offline fallback.
// The playlist is also synced to the authenticated user's server profile so it
// follows the account across devices. Hydration is explicit because the storage
// key and API endpoint depend on bootstrapped user state.

function getMediaDockStorageKey() {
    const userId = getMediaDockGon()?.currentUser?.id
    return `${MEDIA_DOCK_STORAGE_PREFIX}:${userId || 'anon'}`
}

function getMediaDockServerPlaylistUrl() {
    return getMediaDockGon()?.api?.mediaDockPlaylist || '/api/v1/media_dock_playlist'
}

function getMediaDockServerPlaylistItemsUrl() {
    return `${getMediaDockServerPlaylistUrl().replace(/\/$/, '')}/items`
}

function getMediaDockServerPlaylistOrderUrl() {
    return `${getMediaDockServerPlaylistUrl().replace(/\/$/, '')}/order`
}

function getMediaDockServerHistoryItemsUrl() {
    return `${getMediaDockServerPlaylistUrl().replace(/\/$/, '')}/history_items`
}

function getMediaDockServerPageUrl(section, offset) {
    const baseUrl =
        section === 'history'
            ? getMediaDockServerHistoryItemsUrl()
            : getMediaDockServerPlaylistItemsUrl()
    return `${baseUrl}?offset=${offset}&baseRevision=${mediaDockServerPlaylistRevision}`
}

function canSyncMediaDockPlaylistToServer() {
    return Boolean(
        typeof window !== 'undefined' &&
            typeof globalThis.fetch === 'function' &&
            getMediaDockGon()?.currentUser?.id,
    )
}

function getMediaDockServerHeaders() {
    const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    }

    const formToken = getMediaDockGon()?.formToken
    if (formToken) {
        headers['X-CSRF-Token'] = formToken
    }

    try {
        const token = JSON.parse(window.localStorage.getItem('token') || 'null')
        ;['access-token', 'client', 'uid', 'expiry'].forEach(key => {
            if (token?.[key]) {
                headers[key] = token[key]
            }
        })
    } catch {}

    return headers
}

function finiteNonNegativeNumber(value) {
    const number = Number(value)
    return Number.isFinite(number) && number > 0 ? number : 0
}

const ANSWER_VIDEO_KEY_PATTERN = /^answer-video:([^:]+):/
const ANSWER_MEDIA_URL_PATTERN = /\/answers\/([^/?#]+)\//i

function deriveTrackAnswerId(mediaKey, src) {
    const fromKey = ANSWER_VIDEO_KEY_PATTERN.exec(mediaKey || '')?.[1]
    if (fromKey) return fromKey
    return ANSWER_MEDIA_URL_PATTERN.exec(String(src || ''))?.[1] || null
}

function normalizePersistedTrack(stored) {
    const mediaKey = normalizeMediaKey(stored?.mediaKey)
    const src = stored?.src
    if (!mediaKey || !src) return null

    const answerId = stored.answerId || deriveTrackAnswerId(mediaKey, src)
    const artifactOrdinalId = stored.artifactOrdinalId || null
    return {
        mediaKey,
        kind: stored.kind || 'audio',
        src,
        videoId: stored.videoId || null,
        posterSrc: stored.posterSrc || null,
        title: stored.title || null,
        answerId: answerId || null,
        questId: stored.questId || null,
        ...(artifactOrdinalId
            ? {
                  artifactOrdinalId,
                  artifactOwnerId: stored.artifactOwnerId || null,
              }
            : {}),
        currentTime: finiteNonNegativeNumber(stored.currentTime),
        duration: finiteNonNegativeNumber(stored.duration),
    }
}

function normalizeMediaDockPagination(rawPagination, loadedCount = 0) {
    const totalCount = Number(rawPagination?.totalCount)
    const nextOffset = Number(rawPagination?.nextOffset)
    const hasMore = rawPagination?.hasMore === true

    return {
        hasMore,
        nextOffset: hasMore && Number.isFinite(nextOffset) && nextOffset >= 0 ? nextOffset : null,
        totalCount:
            Number.isFinite(totalCount) && totalCount >= loadedCount ? totalCount : loadedCount,
    }
}

function normalizePersistedMediaDockSnapshot(rawSnapshot, options = {}) {
    if (rawSnapshot?.version !== 1 || !Array.isArray(rawSnapshot.tracks)) return null

    const tracks = rawSnapshot.tracks.map(normalizePersistedTrack).filter(Boolean)
    const restoredKeys = new Set(tracks.map(track => track.mediaKey))
    const normalizeKeys = keys =>
        Array.isArray(keys)
            ? keys.map(normalizeMediaKey).filter(mediaKey => mediaKey && restoredKeys.has(mediaKey))
            : []

    const historySource =
        Array.isArray(rawSnapshot.history) || !options.defaultHistoryFromTracks
            ? rawSnapshot.history
            : tracks.map(track => track.mediaKey)
    const updatedAt = Number(rawSnapshot.updatedAt)
    const revision = Number(rawSnapshot.revision)
    const history = normalizeKeys(historySource)
    const playlist = normalizeKeys(rawSnapshot.playlist)

    return {
        updatedAt: Number.isFinite(updatedAt) && updatedAt > 0 ? updatedAt : 0,
        revision: Number.isFinite(revision) && revision >= 0 ? revision : 0,
        tracks,
        history,
        playlist,
        pagination: {
            history: normalizeMediaDockPagination(rawSnapshot.pagination?.history, history.length),
            playlist: normalizeMediaDockPagination(
                rawSnapshot.pagination?.playlist,
                playlist.length,
            ),
        },
    }
}

function normalizeMediaDockItemsPage(rawPage) {
    if (rawPage?.version !== 1 || !Array.isArray(rawPage.tracks)) return null

    const tracks = rawPage.tracks.map(normalizePersistedTrack).filter(Boolean)
    const restoredKeys = new Set(tracks.map(track => track.mediaKey))
    const mediaKeys = Array.isArray(rawPage.mediaKeys)
        ? rawPage.mediaKeys
              .map(normalizeMediaKey)
              .filter(mediaKey => mediaKey && restoredKeys.has(mediaKey))
        : []
    const revision = Number(rawPage.revision)
    const updatedAt = Number(rawPage.updatedAt)
    const offset = Number(rawPage.offset)
    const pagination = normalizeMediaDockPagination(rawPage, offset + mediaKeys.length)

    if (!Number.isFinite(revision) || revision < 0) return null
    if (!Number.isFinite(offset) || offset < 0) return null

    return {
        revision,
        updatedAt: Number.isFinite(updatedAt) && updatedAt > 0 ? updatedAt : 0,
        offset,
        tracks,
        mediaKeys,
        ...pagination,
    }
}

function applyPersistedMediaDockSnapshot(snapshot, options = {}) {
    if (!snapshot) return false

    const restoredKeys = new Set()
    mediaDockRestoringSnapshot = true
    try {
        snapshot.tracks.forEach(stored => {
            const mediaKey = normalizeMediaKey(stored?.mediaKey)
            if (!mediaKey || !stored.src) return

            if (options.overwriteTracks || !tracksByMediaKey.has(mediaKey)) {
                registerMediaTrack(mediaKey, {
                    kind: stored.kind || 'audio',
                    src: stored.src,
                    videoId: stored.videoId || null,
                    posterSrc: stored.posterSrc || null,
                    title: stored.title || null,
                    answerId: stored.answerId || null,
                    questId: stored.questId || null,
                    ...(stored.artifactOrdinalId
                        ? {
                              artifactOrdinalId: stored.artifactOrdinalId,
                              artifactOwnerId: stored.artifactOwnerId || null,
                          }
                        : {}),
                })
            }

            // Seed resume positions, but never clobber state from this session.
            if (!playbackStateByMediaKey.has(mediaKey)) {
                const seeded = {}
                if (Number.isFinite(stored.currentTime) && stored.currentTime > 0) {
                    seeded.currentTime = stored.currentTime
                }
                if (Number.isFinite(stored.duration) && stored.duration > 0) {
                    seeded.duration = stored.duration
                }
                if (Object.keys(seeded).length) {
                    playbackStateByMediaKey.set(mediaKey, seeded)
                }
            }

            restoredKeys.add(mediaKey)
        })

        if (options.includeHistory) {
            // Restored history goes behind anything already played this session.
            snapshot.history.forEach(mediaKey => {
                if (!mediaKey || !restoredKeys.has(mediaKey)) return
                if (!historyMediaKeys.includes(mediaKey)) {
                    historyMediaKeys.push(mediaKey)
                }
            })
        }

        if (options.replaceHistory) {
            historyMediaKeys = snapshot.history.filter(
                mediaKey => mediaKey && restoredKeys.has(mediaKey),
            )
        }

        if (options.replacePlaylist) {
            playlistMediaKeys = snapshot.playlist.filter(
                mediaKey => mediaKey && restoredKeys.has(mediaKey),
            )
            if (options.updatePlaylistTimestamp !== false) {
                mediaDockPlaylistUpdatedAt = snapshot.updatedAt || mediaDockPlaylistUpdatedAt
            }
        }
    } finally {
        mediaDockRestoringSnapshot = false
    }

    return true
}

function buildEmptyMediaDockPlaylistSnapshot(revision = 0) {
    return {
        updatedAt: 0,
        revision,
        tracks: [],
        history: [],
        playlist: [],
        pagination: {
            history: { hasMore: false, nextOffset: null, totalCount: 0 },
            playlist: { hasMore: false, nextOffset: null, totalCount: 0 },
        },
    }
}

function applyServerMediaDockPlaylistSnapshot(serverSnapshot, options = {}) {
    if (!serverSnapshot) return false
    if (serverSnapshot.revision < mediaDockServerPlaylistRevision) return false

    const replaceHistory = options.replaceHistory !== false
    const replacePlaylist = options.replacePlaylist !== false
    applyPersistedMediaDockSnapshot(serverSnapshot, {
        replaceHistory,
        replacePlaylist,
        overwriteTracks: true,
    })
    mediaDockServerPlaylistRevision = serverSnapshot.revision
    mediaDockServerPagination = {
        history: replaceHistory
            ? serverSnapshot.pagination.history
            : mediaDockServerPagination.history,
        playlist: replacePlaylist
            ? serverSnapshot.pagination.playlist
            : mediaDockServerPagination.playlist,
    }
    if (replaceHistory) {
        emitMediaDockEvent({ type: 'history', source: 'server' })
    }
    if (replacePlaylist) {
        emitMediaDockEvent({ type: 'playlist', source: 'server' })
    }
    return true
}

function buildPersistedMediaDockTrack(mediaKey) {
    const track = tracksByMediaKey.get(mediaKey)
    if (!track?.src) return null

    const stored = playbackStateByMediaKey.get(mediaKey)
    return {
        mediaKey,
        kind: track.kind || 'audio',
        src: track.src,
        videoId: track.videoId || null,
        posterSrc: track.posterSrc || null,
        title: track.title || null,
        answerId: track.answerId || null,
        questId: track.questId || null,
        ...(track.artifactOrdinalId
            ? {
                  artifactOrdinalId: track.artifactOrdinalId,
                  artifactOwnerId: track.artifactOwnerId || null,
              }
            : {}),
        currentTime: Number.isFinite(stored?.currentTime) ? stored.currentTime : 0,
        duration: Number.isFinite(stored?.duration) ? stored.duration : 0,
    }
}

function buildMediaDockPersistenceSnapshot(options = {}) {
    const persistedKeys = options.playlistOnly
        ? [...playlistMediaKeys]
        : [...historyMediaKeys, ...playlistMediaKeys.filter(key => !historyMediaKeys.includes(key))]
    const tracks = persistedKeys.map(buildPersistedMediaDockTrack).filter(Boolean)
    const persistedTrackKeys = new Set(tracks.map(track => track.mediaKey))
    const playlist = playlistMediaKeys.filter(mediaKey => persistedTrackKeys.has(mediaKey))
    const updatedAt = options.playlistOnly ? mediaDockPlaylistUpdatedAt || Date.now() : Date.now()

    return {
        version: 1,
        revision: mediaDockServerPlaylistRevision,
        updatedAt,
        tracks,
        ...(options.playlistOnly ? {} : { history: historyMediaKeys }),
        playlist,
    }
}

function saveMediaDockHistoryNow() {
    if (!mediaDockHydrated || typeof window === 'undefined') return

    if (mediaDockSaveTimeout) {
        window.clearTimeout(mediaDockSaveTimeout)
        mediaDockSaveTimeout = null
    }
    setMediaDockQaGauge('persistence.timeoutPending', 0)

    try {
        const serialized = JSON.stringify(buildMediaDockPersistenceSnapshot())
        const diagnosticsEnabled = mediaDockQaDiagnosticsEnabled()
        const startedAt = diagnosticsEnabled
            ? typeof performance === 'undefined'
                ? Date.now()
                : performance.now()
            : 0
        window.localStorage.setItem(getMediaDockStorageKey(), serialized)
        if (diagnosticsEnabled) {
            const completedAt = typeof performance === 'undefined' ? Date.now() : performance.now()
            incrementMediaDockQaCounter('persistence.writes')
            incrementMediaDockQaCounter(
                'persistence.bytes',
                getMediaDockQaUtf8ByteLength(serialized),
            )
            incrementMediaDockQaCounter('persistence.writeDurationMs', completedAt - startedAt)
        }
    } catch {}
}

function scheduleMediaDockHistorySave() {
    if (!mediaDockHydrated || typeof window === 'undefined') return
    if (mediaDockSaveTimeout) {
        window.clearTimeout(mediaDockSaveTimeout)
        incrementMediaDockQaCounter('persistence.timeoutsCancelled')
    }
    incrementMediaDockQaCounter('persistence.timeoutsScheduled')
    setMediaDockQaGauge('persistence.timeoutPending', 1)
    mediaDockSaveTimeout = window.setTimeout(saveMediaDockHistoryNow, MEDIA_DOCK_SAVE_DEBOUNCE_MS)
}

function enqueueMediaDockPlaylistMutation(mutation) {
    if (!mediaDockHydrated || !canSyncMediaDockPlaylistToServer()) return null

    const runMutation = () => mutation().catch(() => null)
    mediaDockPlaylistMutationQueue = mediaDockPlaylistMutationQueue.then(runMutation, runMutation)
    return mediaDockPlaylistMutationQueue
}

async function sendMediaDockPlaylistItemMutation(mutation, options = {}) {
    const method = mutation.type === 'remove' ? 'DELETE' : 'POST'
    const body =
        mutation.type === 'remove'
            ? {
                  baseRevision: mediaDockServerPlaylistRevision,
                  mediaKey: mutation.mediaKey,
              }
            : {
                  baseRevision: mediaDockServerPlaylistRevision,
                  track: mutation.track,
                  position: mutation.position || 'end',
                  afterMediaKey: mutation.afterMediaKey || null,
              }

    const response = await globalThis.fetch(getMediaDockServerPlaylistItemsUrl(), {
        method,
        credentials: 'same-origin',
        headers: getMediaDockServerHeaders(),
        body: JSON.stringify(body),
    })
    if (!response || typeof response.json !== 'function') return null

    const data = await response.json().catch(() => null)
    const serverSnapshot = normalizePersistedMediaDockSnapshot(data?.playlist)
    if (response.status === 409) {
        if (serverSnapshot) {
            applyServerMediaDockPlaylistSnapshot(serverSnapshot)
        }
        if (serverSnapshot && options.retry !== false) {
            return sendMediaDockPlaylistItemMutation(mutation, { retry: false })
        }
        return null
    }

    if (!response.ok) return null
    if (serverSnapshot) {
        applyServerMediaDockPlaylistSnapshot(serverSnapshot, { replaceHistory: false })
    }
    emitAnswerPlaylistCounts(data?.answerPlaylistCounts)
    return serverSnapshot
}

function queueMediaDockPlaylistAdd(mediaKey, options = {}) {
    const track = buildPersistedMediaDockTrack(mediaKey)
    if (!track) return null

    return enqueueMediaDockPlaylistMutation(() =>
        sendMediaDockPlaylistItemMutation({
            type: 'add',
            track,
            position: options.position === 'next' ? 'next' : 'end',
            afterMediaKey: normalizeMediaKey(options.afterMediaKey),
        }),
    )
}

function queueMediaDockPlaylistRemove(mediaKey) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return null

    return enqueueMediaDockPlaylistMutation(() =>
        sendMediaDockPlaylistItemMutation({
            type: 'remove',
            mediaKey: normalizedMediaKey,
        }),
    )
}

async function sendMediaDockPlaylistOrderMutation(playlist, options = {}) {
    const response = await globalThis.fetch(getMediaDockServerPlaylistOrderUrl(), {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: getMediaDockServerHeaders(),
        body: JSON.stringify({
            baseRevision: mediaDockServerPlaylistRevision,
            playlist,
        }),
    })
    if (!response || typeof response.json !== 'function') return null

    const data = await response.json().catch(() => null)
    const serverSnapshot = normalizePersistedMediaDockSnapshot(data?.playlist)
    if (response.status === 409) {
        if (serverSnapshot) {
            applyServerMediaDockPlaylistSnapshot(serverSnapshot)
        }
        if (serverSnapshot && options.retry !== false) {
            return sendMediaDockPlaylistOrderMutation(playlist, { retry: false })
        }
        return null
    }

    if (!response.ok) return null
    if (serverSnapshot) {
        applyServerMediaDockPlaylistSnapshot(serverSnapshot, { replaceHistory: false })
    }
    return serverSnapshot
}

function queueMediaDockPlaylistOrder(playlist) {
    const orderedKeys = Array.isArray(playlist)
        ? playlist
              .map(normalizeMediaKey)
              .filter(mediaKey => mediaKey && tracksByMediaKey.has(mediaKey))
        : []
    if (!orderedKeys.length) return null

    return enqueueMediaDockPlaylistMutation(() => sendMediaDockPlaylistOrderMutation(orderedKeys))
}

async function sendMediaDockHistoryItemMutation(mutation, options = {}) {
    const method = mutation.type === 'remove' ? 'DELETE' : 'POST'
    const body =
        mutation.type === 'remove'
            ? {
                  baseRevision: mediaDockServerPlaylistRevision,
                  mediaKey: mutation.mediaKey,
              }
            : {
                  baseRevision: mediaDockServerPlaylistRevision,
                  track: mutation.track,
              }

    const response = await globalThis.fetch(getMediaDockServerHistoryItemsUrl(), {
        method,
        credentials: 'same-origin',
        headers: getMediaDockServerHeaders(),
        body: JSON.stringify(body),
    })
    if (!response || typeof response.json !== 'function') return null

    const data = await response.json().catch(() => null)
    const serverSnapshot = normalizePersistedMediaDockSnapshot(data?.playlist)
    if (response.status === 409) {
        if (serverSnapshot) {
            applyServerMediaDockPlaylistSnapshot(serverSnapshot)
        }
        if (serverSnapshot && options.retry !== false) {
            return sendMediaDockHistoryItemMutation(mutation, { retry: false })
        }
        return null
    }

    if (!response.ok) return null
    if (serverSnapshot) {
        applyServerMediaDockPlaylistSnapshot(serverSnapshot, { replacePlaylist: false })
    }
    return serverSnapshot
}

function queueMediaDockHistoryAdd(mediaKey) {
    const track = buildPersistedMediaDockTrack(mediaKey)
    if (!track) return null

    return enqueueMediaDockPlaylistMutation(() =>
        sendMediaDockHistoryItemMutation({
            type: 'add',
            track,
        }),
    )
}

function queueMediaDockHistoryRemove(mediaKey) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return null

    return enqueueMediaDockPlaylistMutation(() =>
        sendMediaDockHistoryItemMutation({
            type: 'remove',
            mediaKey: normalizedMediaKey,
        }),
    )
}

export function syncMediaDockPlaylistFromServer(options = {}) {
    if (!mediaDockHydrated || !canSyncMediaDockPlaylistToServer()) return null

    const now = Date.now()
    if (
        !options.force &&
        mediaDockLastServerFetchAt &&
        now - mediaDockLastServerFetchAt < MEDIA_DOCK_SERVER_SYNC_MIN_INTERVAL_MS
    ) {
        return mediaDockServerSyncInFlight
    }
    if (mediaDockServerSyncInFlight) return mediaDockServerSyncInFlight

    mediaDockLastServerFetchAt = now
    mediaDockServerSyncInFlight = globalThis
        .fetch(getMediaDockServerPlaylistUrl(), {
            method: 'GET',
            credentials: 'same-origin',
            headers: getMediaDockServerHeaders(),
        })
        .then(response => {
            if (!response?.ok) return null
            if (typeof response.json !== 'function') return null
            return response.json().catch(() => null)
        })
        .then(data => {
            const serverSnapshot = normalizePersistedMediaDockSnapshot(data?.playlist)
            if (!serverSnapshot) {
                if (mediaDockServerPlaylistRevision === 0) {
                    applyServerMediaDockPlaylistSnapshot(buildEmptyMediaDockPlaylistSnapshot(0))
                }
                return null
            }

            applyServerMediaDockPlaylistSnapshot(serverSnapshot)
            return serverSnapshot
        })
        .catch(() => null)
        .finally(() => {
            mediaDockServerSyncInFlight = null
        })

    return mediaDockServerSyncInFlight
}

export function loadMoreMediaDockItems(section) {
    if (section !== 'history' && section !== 'playlist') return null
    if (!mediaDockHydrated || !canSyncMediaDockPlaylistToServer()) return null

    const pagination = mediaDockServerPagination[section]
    if (!pagination?.hasMore || pagination.nextOffset == null) return null
    if (mediaDockServerPageLoads[section]) return mediaDockServerPageLoads[section]

    const requestedOffset = pagination.nextOffset
    const pageLoad = globalThis
        .fetch(getMediaDockServerPageUrl(section, requestedOffset), {
            method: 'GET',
            credentials: 'same-origin',
            headers: getMediaDockServerHeaders(),
        })
        .then(async response => {
            if (!response || typeof response.json !== 'function') return null
            const data = await response.json().catch(() => null)

            if (response.status === 409) {
                const serverSnapshot = normalizePersistedMediaDockSnapshot(data?.playlist)
                if (serverSnapshot) {
                    applyServerMediaDockPlaylistSnapshot(serverSnapshot)
                }
                return null
            }
            if (!response.ok) return null

            const page = normalizeMediaDockItemsPage(data?.page)
            if (!page) return null
            if (
                page.revision !== mediaDockServerPlaylistRevision ||
                page.offset !== requestedOffset
            ) {
                syncMediaDockPlaylistFromServer({ force: true })
                return null
            }

            const currentKeys = section === 'history' ? historyMediaKeys : playlistMediaKeys
            if (currentKeys.length !== requestedOffset) {
                syncMediaDockPlaylistFromServer({ force: true })
                return null
            }

            applyPersistedMediaDockSnapshot(
                {
                    tracks: page.tracks,
                    history: [],
                    playlist: [],
                },
                { overwriteTracks: true },
            )
            const nextKeys = [
                ...currentKeys,
                ...page.mediaKeys.filter(mediaKey => !currentKeys.includes(mediaKey)),
            ]
            if (section === 'history') {
                historyMediaKeys = nextKeys
            } else {
                playlistMediaKeys = nextKeys
            }
            mediaDockServerPagination = {
                ...mediaDockServerPagination,
                [section]: {
                    hasMore: page.hasMore,
                    nextOffset: page.nextOffset,
                    totalCount: page.totalCount,
                },
            }
            emitMediaDockEvent({ type: section, source: 'server' })
            return page
        })
        .catch(() => null)
        .finally(() => {
            if (mediaDockServerPageLoads[section] === pageLoad) {
                mediaDockServerPageLoads[section] = null
                emitMediaDockEvent({ type: section, source: 'server', loading: false })
            }
        })

    mediaDockServerPageLoads[section] = pageLoad
    emitMediaDockEvent({ type: section, source: 'server', loading: true })
    return pageLoad
}

export function handleMediaDockPlaylistChanged(change = {}) {
    const revision = Number(change?.revision)
    const hasKnownRevision = Number.isFinite(revision) && revision >= 0
    if (hasKnownRevision && revision <= mediaDockServerPlaylistRevision) return null

    if (!mediaDockHydrated) {
        mediaDockPendingRealtimeSync = change
        return null
    }

    return syncMediaDockPlaylistFromServer({ force: true })
}

function flushMediaDockPersistenceNow() {
    saveMediaDockHistoryNow()
}

export function hydrateMediaDockHistory() {
    if (mediaDockHydrated || typeof window === 'undefined') return
    mediaDockHydrated = true

    let localSnapshot = null
    try {
        const parsed = JSON.parse(window.localStorage.getItem(getMediaDockStorageKey()) || 'null')
        localSnapshot = normalizePersistedMediaDockSnapshot(parsed, {
            defaultHistoryFromTracks: true,
        })
    } catch {}

    if (localSnapshot) {
        mediaDockPlaylistUpdatedAt = localSnapshot.playlist.length ? localSnapshot.updatedAt : 0
        applyPersistedMediaDockSnapshot(localSnapshot, {
            includeHistory: true,
            replacePlaylist: true,
            updatePlaylistTimestamp: localSnapshot.playlist.length > 0,
        })
    }

    window.addEventListener('pagehide', flushMediaDockPersistenceNow)
    window.addEventListener('focus', () => syncMediaDockPlaylistFromServer())
    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                syncMediaDockPlaylistFromServer()
            }
        })
    }

    emitMediaDockEvent({ type: 'history' })
    const pendingRealtimeSync = mediaDockPendingRealtimeSync
    mediaDockPendingRealtimeSync = null
    if (pendingRealtimeSync) {
        handleMediaDockPlaylistChanged(pendingRealtimeSync)
    } else {
        syncMediaDockPlaylistFromServer({ force: true })
    }
}

function findPlayingOwnerForMediaKey(mediaKey, options = {}) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return null
    const exceptOwnerId = options.exceptOwnerId ? String(options.exceptOwnerId) : null
    const ownerIdPredicate =
        typeof options.ownerIdPredicate === 'function' ? options.ownerIdPredicate : null

    for (const [ownerId, owner] of ownersById) {
        if (exceptOwnerId && String(ownerId) === exceptOwnerId) continue
        if (ownerIdPredicate && !ownerIdPredicate(ownerId)) continue
        if (
            owner.mediaKey === normalizedMediaKey &&
            isOwnerConnected(owner) &&
            !isOwnerPaused(owner) &&
            !isOwnerAtEnd(owner)
        ) {
            return { ownerId, ...owner }
        }
    }
    return null
}

export function subscribeToMediaDock(listener) {
    if (typeof listener !== 'function') return () => {}
    mediaDockListeners.add(listener)
    return () => mediaDockListeners.delete(listener)
}

export function registerMediaTrack(mediaKey, meta = {}) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey || !meta.src) return

    const existing = tracksByMediaKey.get(normalizedMediaKey) || {}
    const merged = {
        ...existing,
        ...meta,
        mediaKey: normalizedMediaKey,
    }
    // Registrations without an answer id must not clobber a known one, and
    // answer media can often be identified from the key or media URL.
    merged.answerId =
        meta.answerId ||
        existing.answerId ||
        deriveTrackAnswerId(normalizedMediaKey, merged.src) ||
        null
    merged.questId = meta.questId || existing.questId || null

    tracksByMediaKey.set(normalizedMediaKey, merged)
    syncMediaDockQaRegistryGauges()
    if (
        !mediaDockRestoringSnapshot &&
        mediaDockHydrated &&
        playlistMediaKeys.includes(normalizedMediaKey)
    ) {
        scheduleMediaDockHistorySave()
    }

    const activeOwner = getOwner(activeOwnerId)
    if (
        activeOwnerId &&
        activeOwner?.mediaKey === normalizedMediaKey &&
        isOwnerConnected(activeOwner) &&
        !isOwnerPaused(activeOwner) &&
        !isOwnerAtEnd(activeOwner) &&
        (nowPlayingMediaKey !== normalizedMediaKey || !nowPlayingIsPlaying)
    ) {
        claimDockTrack(normalizedMediaKey, activeOwnerId)
    }
}

// The registered track for an answer's media, if any player has mounted it.
export function findMediaTrackForAnswer(answerId, kind = null) {
    if (!answerId) return null
    const wanted = String(answerId)
    for (const track of tracksByMediaKey.values()) {
        if (
            track?.answerId &&
            String(track.answerId) === wanted &&
            (!kind || track.kind === kind)
        ) {
            return track
        }
    }
    return null
}

export function getMediaTrack(mediaKey) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return null
    return tracksByMediaKey.get(normalizedMediaKey) || null
}

// A media key can be mounted in several places at once (e.g. the same
// payload video in a stream post and in the open thread), so origins are a
// set, and lookups prefer the instance the user can actually see.
export function setMediaTrackOrigin(mediaKey, originEl) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey || !originEl) return

    let origins = trackOriginsByMediaKey.get(normalizedMediaKey)
    if (!origins) {
        origins = new Set()
        trackOriginsByMediaKey.set(normalizedMediaKey, origins)
    }
    origins.add(originEl)
    syncMediaDockQaRegistryGauges()
}

export function clearMediaTrackOrigin(mediaKey, originEl) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return

    const origins = trackOriginsByMediaKey.get(normalizedMediaKey)
    if (!origins) return

    if (originEl) {
        // A state-preserving hosted surface may physically live inside this
        // origin. Give the dock a synchronous chance to move it home before
        // React removes the origin and disconnects the playing iframe.
        if (
            mediaDockHostedPlacement?.mediaKey === normalizedMediaKey &&
            mediaDockHostedPlacement.originEl === originEl
        ) {
            emitMediaDockEvent({
                type: 'hostedOriginWillClear',
                mediaKey: normalizedMediaKey,
                originEl,
            })
        }
        origins.delete(originEl)
    } else {
        origins.clear()
    }
    if (!origins.size) {
        trackOriginsByMediaKey.delete(normalizedMediaKey)
    }
    syncMediaDockQaRegistryGauges()
}

export function getMediaTrackOriginElement(mediaKey, options = {}) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    const origins = trackOriginsByMediaKey.get(normalizedMediaKey)
    if (!origins?.size) return null

    let firstConnected = null
    for (const originEl of origins) {
        if (!originEl?.isConnected) continue
        if (!firstConnected) firstConnected = originEl
        if (isElementUserVisible(originEl, { mediaKey: normalizedMediaKey })) return originEl
    }
    return options.visibleOnly ? null : firstConnected
}

export function getVisibleMediaTrackOrigin(mediaKey, options = {}) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    const origins = trackOriginsByMediaKey.get(normalizedMediaKey)
    if (!origins?.size) return null

    for (const element of origins) {
        if (options.exclude && element === options.exclude) continue
        const visibility = getElementUserVisibilitySnapshot(element, {
            mediaKey: normalizedMediaKey,
        })
        if (visibility.isVisible) {
            return { element, rect: visibility.rect }
        }
    }

    return null
}

export function getMediaTrackOriginRect(mediaKey) {
    const originEl = getMediaTrackOriginElement(mediaKey)
    if (!originEl) return null

    incrementMediaDockQaCounter('layout.boundingClientRectReads')
    const rect = originEl.getBoundingClientRect()
    return rect.width > 0 && rect.height > 0 ? rect : null
}

// --- Hosted playback -------------------------------------------------------
// With hosted playback the dock's own element is the ONLY runtime for a track:
// it is positioned over the inline post's media frame while that frame is on
// screen and glides to the dock corner when it is not. Nothing is preloaded
// twice or handed off, so navigation cannot interrupt playback. This registry
// lets inline surfaces learn whether the runtime is currently sitting over
// their frame and find the dock's chrome layer to portal their controls into.

let mediaDockHostedPlacement = null // { mediaKey, originEl }
let mediaDockHostedChromeLayer = null
let mediaDockHostedSurfaceElement = null

// Hosted placement is sampled every animation frame, so it reuses a
// caller-owned cache. Two things make a naive per-frame read expensive: the
// sidebar occlusion test walks ancestors calling getComputedStyle, and an
// origin that has not moved still costs a full bounds recomputation. The
// sidebar is fixed chrome (it cannot move during a scroll or a panel slide),
// and a still origin needs no bounds work at all — only a periodic refresh in
// case chrome outside the origin changed.
const PLACEMENT_OCCLUSION_CACHE_MS = 200
const PLACEMENT_BOUNDS_TTL_MS = 300

export function createMediaTrackPlacementCache() {
    return {
        element: null,
        root: null,
        rootFor: null,
        occlusionRect: null,
        occlusionAt: 0,
        // Rounded origin geometry, kept as numbers: the still-frame check runs
        // every frame, so it must not allocate.
        left: null,
        top: null,
        width: null,
        height: null,
        bounds: null,
        visibleRect: null,
        visible: false,
        boundsAt: 0,
    }
}

function readCachedSidebarOcclusion(cache, now) {
    if (!cache) return getLeftSidebarOcclusionRect()
    if (cache.occlusionAt && now - cache.occlusionAt < PLACEMENT_OCCLUSION_CACHE_MS) {
        return cache.occlusionRect
    }

    cache.occlusionRect = getLeftSidebarOcclusionRect()
    cache.occlusionAt = now
    return cache.occlusionRect
}

function readCachedVisibilityRoot(cache, element) {
    if (!cache) return resolveVisibilityRoot(element)
    if (cache.rootFor !== element) {
        cache.rootFor = element
        cache.root = resolveVisibilityRoot(element)
    }
    return cache.root
}

// The placement is written to the DOM rounded to whole pixels, so sub-pixel
// drift is not a move.
function placementMatchesCache(cache, element, rect, now) {
    return Boolean(
        cache &&
            cache.element === element &&
            cache.left === Math.round(rect.left) &&
            cache.top === Math.round(rect.top) &&
            cache.width === Math.round(rect.width) &&
            cache.height === Math.round(rect.height) &&
            now - cache.boundsAt < PLACEMENT_BOUNDS_TTL_MS,
    )
}

/**
 * Where a track's inline origin frame is right now, plus the bounds the user
 * can see it through. Prefers the caller's own origin element when it is still
 * mounted; falls back to any mounted origin for the key.
 *
 * Pass a `cache` from `createMediaTrackPlacementCache()` when sampling per
 * frame: a frame whose origin has not moved costs a single
 * `getBoundingClientRect`, and `unchanged: true` tells the caller its last
 * placement still holds, so it can skip the style write too.
 */
export function getMediaTrackOriginPlacement(mediaKey, options = {}) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    const origins = normalizedMediaKey ? trackOriginsByMediaKey.get(normalizedMediaKey) : null
    if (!origins?.size) return null

    const preferred = options.originEl || null
    let element = preferred && preferred.isConnected && origins.has(preferred) ? preferred : null
    if (!element) {
        for (const originEl of origins) {
            if (originEl?.isConnected) {
                element = originEl
                break
            }
        }
    }
    if (!element) return null

    const cache = options.cache || null
    const now = Date.now()
    // A zero-sized rect means the frame is not rendered (display:none, detached
    // subtree), which is exactly what the caller treats as "hidden".
    const rect = getElementRect(element)
    if (!rect) {
        if (cache) cache.element = null
        return { element, rect: null, bounds: null, visibleRect: null, visible: false }
    }

    if (placementMatchesCache(cache, element, rect, now)) {
        return {
            element,
            rect,
            bounds: cache.bounds,
            visibleRect: cache.visibleRect,
            visible: cache.visible,
            unchanged: true,
        }
    }

    let bounds = getViewportRect()
    const root = readCachedVisibilityRoot(cache, element)
    if (bounds && root) {
        bounds = intersectRects(bounds, getElementRect(root))
    }
    bounds = applyOcclusionRect(bounds, readCachedSidebarOcclusion(cache, now))

    const visibleRect = intersectRects(rect, bounds)
    const visible = Boolean(visibleRect)
    if (cache) {
        cache.element = element
        cache.left = Math.round(rect.left)
        cache.top = Math.round(rect.top)
        cache.width = Math.round(rect.width)
        cache.height = Math.round(rect.height)
        cache.bounds = bounds
        cache.visibleRect = visibleRect
        cache.visible = visible
        cache.boundsAt = now
    }

    return { element, rect, bounds, visibleRect, visible, unchanged: false }
}

export function setMediaDockHostedPlacement(placement) {
    const next =
        placement?.mediaKey && placement?.originEl
            ? {
                  mediaKey: normalizeMediaKey(placement.mediaKey),
                  originEl: placement.originEl,
                  kind: placement.kind === 'silent' ? 'silent' : 'visual',
              }
            : null
    const previous = mediaDockHostedPlacement
    const changed =
        (next?.mediaKey || null) !== (previous?.mediaKey || null) ||
        (next?.originEl || null) !== (previous?.originEl || null) ||
        (next?.kind || null) !== (previous?.kind || null)

    mediaDockHostedPlacement = next
    if (changed) {
        emitMediaDockEvent({ type: 'hostedPlacement', mediaKey: next?.mediaKey || null })
    }
    return changed
}

export function getMediaDockHostedPlacement() {
    return mediaDockHostedPlacement
}

export function isMediaOriginHostedInline(mediaKey, originEl) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey || !originEl || !mediaDockHostedPlacement) return false
    return (
        mediaDockHostedPlacement.mediaKey === normalizedMediaKey &&
        mediaDockHostedPlacement.originEl === originEl
    )
}

export function setMediaDockHostedChromeLayer(element) {
    const next = element || null
    if (next === mediaDockHostedChromeLayer) return
    mediaDockHostedChromeLayer = next
    emitMediaDockEvent({
        type: 'hostedChromeLayer',
        mediaKey: mediaDockHostedPlacement?.mediaKey || null,
    })
}

export function getMediaDockHostedChromeLayer() {
    return mediaDockHostedChromeLayer
}

export function setMediaDockHostedSurfaceElement(element) {
    mediaDockHostedSurfaceElement = element || null
}

export function getMediaDockHostedSurfaceElement() {
    return mediaDockHostedSurfaceElement
}

/**
 * An inline surface asks the dock to play a registered track with the dock's
 * own runtime. `placement` says how it is presented while the post is on
 * screen: 'visual' parks the dock over `originEl`, 'silent' keeps the dock
 * hidden (audio has no picture to place). Returns false when the track is
 * unknown (the caller registers it first).
 */
export function requestHostedMediaPlayback(mediaKey, options = {}) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey || !tracksByMediaKey.has(normalizedMediaKey)) return false

    emitMediaDockEvent({
        type: 'command',
        command: 'hostedPlay',
        mediaKey: normalizedMediaKey,
        originEl: options.originEl || null,
        placement: options.placement === 'silent' ? 'silent' : 'visual',
        startAtBeginning: options.startAtBeginning === true,
        resume: options.resume === true,
        source: options.source || 'inline',
    })
    return true
}

export function setMediaKeyPlaybackRate(mediaKey, playbackRate) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    const rate = Number(playbackRate)
    if (!normalizedMediaKey || !Number.isFinite(rate) || rate <= 0) return

    setMediaPlaybackState(normalizedMediaKey, { playbackRate: rate })
    ownersById.forEach(owner => {
        if (owner.mediaKey !== normalizedMediaKey) return
        try {
            if (typeof owner.setPlaybackRate === 'function') {
                owner.setPlaybackRate(rate)
            } else if (owner.mediaEl) {
                owner.mediaEl.playbackRate = rate
            }
        } catch {}
    })
}

/**
 * The CC toggle: remembered per track (so a remounted runtime shows the same
 * captions) and applied to every mounted owner — a controller owner (YouTube)
 * through its hook, a native element through its text tracks.
 */
export function setMediaKeyCaptionsEnabled(mediaKey, enabled) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey || typeof enabled !== 'boolean') return false

    let accepted = true
    let controllerPending = false
    ownersById.forEach(owner => {
        if (owner.mediaKey !== normalizedMediaKey) return
        try {
            if (typeof owner.setCaptionsEnabled === 'function') {
                if (owner.setCaptionsEnabled(enabled) === false) accepted = false
                if (enabled && !readOwnerCaptionsState(owner).enabled) controllerPending = true
            } else if (owner.mediaEl) {
                applyMediaElementCaptions(owner.mediaEl, enabled)
            }
        } catch {
            accepted = false
        }
    })

    if (accepted && !controllerPending) {
        setMediaPlaybackState(normalizedMediaKey, { captions: enabled })
    }
    return accepted
}

export function announceMediaPlaybackControlsChanged(mediaKey) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return
    emitMediaDockEvent({ type: 'controls', mediaKey: normalizedMediaKey })
}

function readOwnerCaptionsState(owner) {
    if (typeof owner.getCaptionsState === 'function') {
        try {
            const state = owner.getCaptionsState()
            return {
                available: state?.available === true,
                enabled: state?.enabled === true,
            }
        } catch {
            return { available: false, enabled: false }
        }
    }
    return readMediaElementCaptionsState(owner.mediaEl)
}

/**
 * Control-surface readout for an owner (paused/volume/rate/captions), used by
 * inline chrome that drives a hosted runtime instead of its own player, and
 * by the dock's own stage.
 */
export function getMediaOwnerControlSnapshot(owner) {
    if (!owner) return null

    const read = (fn, fallback) => {
        try {
            const value = fn()
            return value == null ? fallback : value
        } catch {
            return fallback
        }
    }
    const mediaEl = owner.mediaEl
    const volume =
        typeof owner.getVolume === 'function'
            ? read(owner.getVolume, null)
            : Number.isFinite(mediaEl?.volume)
            ? mediaEl.volume
            : null
    const playbackRate =
        typeof owner.getPlaybackRate === 'function'
            ? read(owner.getPlaybackRate, null)
            : Number.isFinite(mediaEl?.playbackRate)
            ? mediaEl.playbackRate
            : null
    const playbackRates =
        typeof owner.getAvailablePlaybackRates === 'function'
            ? read(owner.getAvailablePlaybackRates, null)
            : null

    const captions = readOwnerCaptionsState(owner)

    return {
        isPaused: isOwnerPaused(owner),
        isEnded: isOwnerAtEnd(owner),
        volume: Number.isFinite(volume) ? volume : null,
        playbackRate: Number.isFinite(playbackRate) && playbackRate > 0 ? playbackRate : null,
        playbackRates: Array.isArray(playbackRates) ? playbackRates : null,
        captionsAvailable: captions.available,
        captionsEnabled: captions.enabled,
    }
}

export function getMediaDockSnapshot() {
    reconcileNowPlayingIsPlaying()
    const history = historyMediaKeys.map(key => tracksByMediaKey.get(key)).filter(Boolean)
    const playlist = playlistMediaKeys.map(key => tracksByMediaKey.get(key)).filter(Boolean)
    if (mediaDockQaDiagnosticsEnabled()) {
        incrementMediaDockQaCounter('snapshot.builds')
        incrementMediaDockQaCounter('snapshot.historyListAllocations')
        incrementMediaDockQaCounter('snapshot.playlistListAllocations')
        if (history !== lastQaHistoryList) {
            incrementMediaDockQaCounter('snapshot.historyIdentityChanges')
        }
        if (playlist !== lastQaPlaylistList) {
            incrementMediaDockQaCounter('snapshot.playlistIdentityChanges')
        }
        lastQaHistoryList = history
        lastQaPlaylistList = playlist
    }

    return {
        nowPlayingMediaKey,
        isPlaying: nowPlayingIsPlaying,
        nowPlayingTrack: getMediaTrack(nowPlayingMediaKey),
        handoff: getMediaHandoffInfo(),
        history,
        playlist,
        pagination: {
            history: {
                ...mediaDockServerPagination.history,
                isLoading: Boolean(mediaDockServerPageLoads.history),
            },
            playlist: {
                ...mediaDockServerPagination.playlist,
                isLoading: Boolean(mediaDockServerPageLoads.playlist),
            },
        },
    }
}

export function isMediaTrackInPlaylist(mediaKey) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    return Boolean(normalizedMediaKey && playlistMediaKeys.includes(normalizedMediaKey))
}

// The post's listener count (Answer#playlist_count) the server returned after
// this viewer's add or remove. The dock writes it into the cached answer, which
// newer answer JSON then replaces as usual.
function emitAnswerPlaylistCounts(counts) {
    if (!counts || typeof counts !== 'object' || !Object.keys(counts).length) return
    emitMediaDockEvent({ type: 'answerPlaylistCounts', counts })
}

export function addMediaTrackToPlaylist(mediaKey, options = {}) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey || !tracksByMediaKey.has(normalizedMediaKey)) return false

    const { position = 'end', moveExisting = false } = options
    const wasInPlaylist = playlistMediaKeys.includes(normalizedMediaKey)
    if (wasInPlaylist && !moveExisting) return false
    if (position === 'next' && wasInPlaylist && normalizedMediaKey === nowPlayingMediaKey) {
        return false
    }

    const nextPlaylist = playlistMediaKeys.filter(key => key !== normalizedMediaKey)
    let afterMediaKey = null
    if (position === 'next') {
        const nowPlayingIndex = nowPlayingMediaKey ? nextPlaylist.indexOf(nowPlayingMediaKey) : -1
        if (nowPlayingIndex >= 0) {
            nextPlaylist.splice(nowPlayingIndex + 1, 0, normalizedMediaKey)
            afterMediaKey = nowPlayingMediaKey
        } else {
            nextPlaylist.unshift(normalizedMediaKey)
        }
    } else {
        nextPlaylist.push(normalizedMediaKey)
    }

    if (nextPlaylist.join('\u0000') !== playlistMediaKeys.join('\u0000')) {
        playlistMediaKeys = nextPlaylist
        mediaDockPlaylistUpdatedAt = Date.now()
        emitMediaDockEvent({ type: 'playlist' })
        queueMediaDockPlaylistAdd(normalizedMediaKey, {
            position,
            afterMediaKey,
        })
    }

    return !wasInPlaylist
}

export function removeMediaTrackFromPlaylist(mediaKey) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return
    const nextPlaylist = playlistMediaKeys.filter(key => key !== normalizedMediaKey)
    if (nextPlaylist.join('\u0000') === playlistMediaKeys.join('\u0000')) return

    playlistMediaKeys = nextPlaylist
    mediaDockPlaylistUpdatedAt = Date.now()
    emitMediaDockEvent({ type: 'playlist' })
    queueMediaDockPlaylistRemove(normalizedMediaKey)
}

export function moveMediaTrackToPlaylistTop(mediaKey) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey || !playlistMediaKeys.includes(normalizedMediaKey)) return false
    if (playlistMediaKeys[0] === normalizedMediaKey) return false

    playlistMediaKeys = [
        normalizedMediaKey,
        ...playlistMediaKeys.filter(key => key !== normalizedMediaKey),
    ]
    mediaDockPlaylistUpdatedAt = Date.now()
    emitMediaDockEvent({ type: 'playlist' })
    queueMediaDockPlaylistOrder(playlistMediaKeys)
    return true
}

export function focusMediaDockTrack(mediaKey, options = {}) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey || !tracksByMediaKey.has(normalizedMediaKey)) return false

    nowPlayingMediaKey = normalizedMediaKey
    nowPlayingIsPlaying = Boolean(findPlayingOwnerForMediaKey(normalizedMediaKey))
    if (options.emit !== false) {
        emitMediaDockEvent({ type: 'focus', mediaKey: normalizedMediaKey })
    }
    return true
}

export function requestMediaDockPlaylistFocus(options = {}) {
    emitMediaDockEvent({
        type: 'command',
        command: 'focusPlaylist',
        mediaKey: normalizeMediaKey(options.mediaKey),
        source: options.source || null,
    })
}

// Open the dock on its History tab (optionally centred on a track). An aired
// radio play uses it: the song is over on the station, but it is in history.
export function requestMediaDockHistoryFocus(options = {}) {
    emitMediaDockEvent({
        type: 'command',
        command: 'focusHistory',
        mediaKey: normalizeMediaKey(options.mediaKey),
        source: options.source || null,
    })
}

export function requestMediaDockRadioFocus(options = {}) {
    emitMediaDockEvent({
        type: 'command',
        command: 'focusRadio',
        source: options.source || null,
    })
}

// Ask the dock to leave the broadcast: radio mode off and playback paused for
// this listener. The station keeps playing without them.
export function requestMediaDockRadioStop(options = {}) {
    emitMediaDockEvent({
        type: 'command',
        command: 'stopRadio',
        source: options.source || null,
    })
}

// Whether this tab's dock is tuned into the station — its radio mode, with
// the dock open. The dock publishes it so surfaces outside the dock (the ON
// AIR card's tuner) can reflect it; it rides the same channel every dock
// subscriber already listens to, and the dock's own handler ignores it.
let mediaDockRadioActive = false

export function setMediaDockRadioActive(active) {
    const next = Boolean(active)
    if (next === mediaDockRadioActive) return
    mediaDockRadioActive = next
    emitMediaDockEvent({ type: 'radioActive', active: next })
}

export function isMediaDockRadioActive() {
    return mediaDockRadioActive
}

// Ask the dock to play a registered track from a surface with no player of
// its own (a play button on a card). The dock adopts the track exactly as it
// does for its playlist rows.
export function requestMediaDockPlay(options = {}) {
    const mediaKey = normalizeMediaKey(options.mediaKey)
    if (!mediaKey || !tracksByMediaKey.has(mediaKey)) return false

    emitMediaDockEvent({
        type: 'command',
        command: 'playTrack',
        mediaKey,
        source: options.source || null,
    })
    return true
}

// Ask the dock to take a registered track over and show it: a media
// dropdown's "Play in Dock" row. The dock decides the tab (a station play
// lands on Radio, following the station). Returns false when nothing
// registered the key.
export function requestMediaDockPlayInDock(options = {}) {
    const mediaKey = normalizeMediaKey(options.mediaKey)
    if (!mediaKey || !tracksByMediaKey.has(mediaKey)) return false

    emitMediaDockEvent({
        type: 'command',
        command: 'playInDock',
        mediaKey,
        source: options.source || null,
    })
    return true
}

// Ask the dock to show a registered track in cinema mode: its stage fills the
// app window (the browser stays out of fullscreen) while the dock's own
// runtime plays the track. A media dropdown's "Cinema" row sends this.
export function requestMediaDockCinema(options = {}) {
    if (!mediaDockCinemaEnabled()) return false

    const mediaKey = normalizeMediaKey(options.mediaKey)
    if (!mediaKey || !tracksByMediaKey.has(mediaKey)) return false

    emitMediaDockEvent({
        type: 'command',
        command: 'cinema',
        mediaKey,
        source: options.source || null,
        originEl: options.originEl || null,
        returnFocusEl: options.returnFocusEl || null,
        returnFocusSelector: options.returnFocusSelector || null,
    })
    return true
}

// The dock left cinema mode for a track it took over only to show it there.
// The post's own surface, if it is still on screen, answers by taking
// playback back the way its "Play here" does, so the picture returns to the
// post the viewer came from instead of stranding in the corner dock.
export function announceMediaDockCinemaExit(mediaKey, originEl) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey || !originEl) return
    emitMediaDockEvent({ type: 'cinemaExit', mediaKey: normalizedMediaKey, originEl })
}

// A playing hosted runtime just left its post because the user navigated
// (swiped panels, opened the post's thread) and another post on screen shows
// the same media. That post answers like its "Play here", so the runtime is
// re-parked over it instead of staying docked in the corner.
export function announceMediaDockHostedReturn(mediaKey, originEl) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey || !originEl) return
    emitMediaDockEvent({ type: 'hostedReturn', mediaKey: normalizedMediaKey, originEl })
}

/**
 * A specific owner's registry entry, while its runtime is mounted. The dock
 * reads its own runtime this way: with the picture in cinema and the post's
 * copy still mounted, "the mounted owner for the key" would otherwise fall
 * back to whichever is paused less recently.
 */
export function getMediaPlaybackOwner(ownerId) {
    if (!ownerId) return null
    const owner = ownersById.get(ownerId)
    return owner && isOwnerConnected(owner) ? { ownerId, ...owner } : null
}

export function findMountedOwnerForMediaKey(mediaKey) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return null

    // Several owners can be mounted for the same key (e.g. an inline player in
    // an offscreen panel plus the dock's own element); prefer the playing one,
    // then the owner inside the visible origin, then any connected owner.
    const mountedOwners = []
    for (const [ownerId, owner] of ownersById) {
        if (owner.mediaKey !== normalizedMediaKey || !isOwnerConnected(owner)) continue

        if (!isOwnerPaused(owner) && !isOwnerAtEnd(owner)) {
            return { ownerId, ...owner }
        }
        mountedOwners.push({ ownerId, ...owner })
    }
    if (!mountedOwners.length) return null

    const visibleOrigin = getMediaTrackOriginElement(normalizedMediaKey, { visibleOnly: true })
    const visibleOwner = visibleOrigin
        ? mountedOwners.find(
              owner => visibleOrigin === owner.mediaEl || visibleOrigin.contains?.(owner.mediaEl),
          )
        : null
    return visibleOwner || mountedOwners[0]
}

// True when the element is rendered and at least partially inside the
// viewport — i.e. the user can actually see it. Offscreen panels, collapsed
// containers, and scrolled-away posts all fail this check.
function getElementUserVisibilitySnapshot(element, options = {}) {
    incrementMediaDockQaCounter('visibility.calls')
    if (typeof window === 'undefined' || !element?.isConnected) {
        return { isVisible: false, rect: null }
    }

    // Fullscreen content is rendered in the browser's top layer rather than
    // inside the inline panel geometry. Keep its registered origin visible to
    // dock decisions, including iOS native video fullscreen where the
    // document fullscreen element remains unset.
    const fullscreenSurface = getFullscreenPlaybackSurface(element, options.mediaKey)
    if (fullscreenSurface) {
        return {
            isVisible: true,
            rect: getElementRect(fullscreenSurface) || getViewportRect(),
        }
    }

    if (typeof element.getClientRects === 'function') {
        incrementMediaDockQaCounter('layout.clientRectReads')
    }
    if (typeof element.getClientRects === 'function' && element.getClientRects().length === 0) {
        return { isVisible: false, rect: null }
    }
    if (isElementHiddenByRenderedStyle(element)) return { isVisible: false, rect: null }

    const rect = getElementRect(element)
    if (!rect?.width || !rect.height) return { isVisible: false, rect: null }

    return {
        isVisible: Boolean(intersectRects(rect, getElementVisibilityBounds(element, options))),
        rect,
    }
}

export function isElementUserVisible(element, options = {}) {
    return getElementUserVisibilitySnapshot(element, options).isVisible
}

export function playMediaKey(mediaKey, options = {}) {
    const owner = findMountedOwnerForMediaKey(mediaKey)
    return owner?.ownerId ? playMediaPlaybackOwner(owner.ownerId, options) : false
}

export function playMediaPlaybackOwner(ownerId, options = {}) {
    const owner = getOwner(ownerId)
    if (!owner || !isOwnerConnected(owner)) return false

    const { transferFromDock = false, startAtBeginning = false, handoffToken = null } = options
    if (handoffToken != null) {
        implicitMediaHandoffTokenByOwnerId.set(ownerId, handoffToken)
    } else {
        // Calls without a handoff token are explicit playback commands. Clear
        // any stale implicit token and supersede a transition owned by some
        // other source so a real user retry is never refused.
        implicitMediaHandoffTokenByOwnerId.delete(ownerId)
        const handoff = getMediaHandoff()
        if (handoff && handoff.sourceOwnerId !== ownerId) {
            cancelMediaHandoff(handoff.token, 'superseded')
        }
    }
    const dockTransferOwnerId =
        transferFromDock &&
        activeOwnerId &&
        activeOwnerId !== ownerId &&
        isDockOwnerId(activeOwnerId)
            ? activeOwnerId
            : null
    const dockTransferOwner = dockTransferOwnerId ? getOwner(dockTransferOwnerId) : null
    const keepDockHandoffSourcePlaying = isPlaybackGatedInlineHandoffFromOwner(
        getMediaHandoff(),
        handoffToken,
        dockTransferOwnerId,
        owner.mediaKey,
    )

    if (dockTransferOwner?.mediaKey) {
        persistOwnerPlaybackState(dockTransferOwner)
    }

    if (startAtBeginning) {
        seekOwnerToTime(owner, 0)
        setMediaPlaybackState(owner.mediaKey, { currentTime: 0 })
    } else {
        const stored = getMediaPlaybackState(owner.mediaKey)
        if (Number.isFinite(stored?.currentTime) && stored.currentTime > 0.05) {
            const duration = safeOwnerDuration(owner, 0)
            if (!duration || stored.currentTime < duration - 0.25) {
                seekOwnerToTime(owner, stored.currentTime)
            }
        }
    }

    if (playOwner(owner)) {
        if (
            dockTransferOwnerId &&
            activeOwnerId === dockTransferOwnerId &&
            !keepDockHandoffSourcePlaying
        ) {
            pauseOwner(dockTransferOwnerId)
            releaseMediaPlayback(dockTransferOwnerId)
        }
        return true
    }

    if (handoffToken != null && implicitMediaHandoffTokenByOwnerId.get(ownerId) === handoffToken) {
        implicitMediaHandoffTokenByOwnerId.delete(ownerId)
    }
    return false
}

export function pauseMediaKey(mediaKey, exceptOwnerId = null) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return

    ownersById.forEach((owner, ownerId) => {
        if (exceptOwnerId && ownerId === exceptOwnerId) return
        if (owner.mediaKey !== normalizedMediaKey || isOwnerPaused(owner)) return

        try {
            if (typeof owner.pause === 'function') {
                owner.pause()
            } else if (typeof owner.mediaEl?.pause === 'function') {
                owner.mediaEl.pause()
            }
        } catch {}
    })
}

export function setMediaKeyVolume(mediaKey, volume, options = {}) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey || !Number.isFinite(volume)) return
    if (options.transferFromDock && options.ownerId) {
        transferMediaPlaybackControlFromDock(options.ownerId)
    }
    const clamped = Math.max(0, Math.min(1, volume))
    const previousSnapshot = getMediaPlaybackState(normalizedMediaKey)

    if (
        !Number.isFinite(previousSnapshot?.volume) ||
        Math.abs(previousSnapshot.volume - clamped) > 0.001
    ) {
        setMediaPlaybackState(normalizedMediaKey, { volume: clamped })
    } else if (!options.forceOwnerSync && !options.transferFromDock) {
        return
    }

    ownersById.forEach(owner => {
        if (owner.mediaKey !== normalizedMediaKey) return
        setOwnerVolume(owner, clamped)
    })
}

export function seekMediaKey(mediaKey, nextTime, options = {}) {
    if (!Number.isFinite(nextTime) || nextTime < 0) return
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return
    if (options.transferFromDock && options.ownerId) {
        transferMediaPlaybackControlFromDock(options.ownerId)
    }

    // Seek every mounted element for the key so a paused duplicate (inline vs
    // dock-owned) does not resume later from a stale position.
    ownersById.forEach(owner => {
        if (owner.mediaKey !== normalizedMediaKey || !isOwnerConnected(owner)) return
        seekOwnerToTime(owner, nextTime)
    })
    setMediaPlaybackState(mediaKey, { currentTime: nextTime })
}

export function removeMediaTrackFromHistory(mediaKey) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey) return

    historyMediaKeys = historyMediaKeys.filter(key => key !== normalizedMediaKey)
    if (nowPlayingMediaKey === normalizedMediaKey) {
        pauseMediaKey(normalizedMediaKey)
        nowPlayingMediaKey = null
        nowPlayingIsPlaying = false
    }
    queueMediaDockHistoryRemove(normalizedMediaKey)
    emitMediaDockEvent({ type: 'history' })
}

export function clearMediaDockHistory() {
    const retainedMediaKey = nowPlayingMediaKey
    const removedMediaKeys = historyMediaKeys.filter(mediaKey => mediaKey !== retainedMediaKey)
    historyMediaKeys = nowPlayingMediaKey ? [nowPlayingMediaKey] : []
    removedMediaKeys.forEach(mediaKey => queueMediaDockHistoryRemove(mediaKey))
    emitMediaDockEvent({ type: 'history' })
}

export function subscribeToMediaPlaybackState(mediaKey, listener) {
    const normalizedMediaKey = normalizeMediaKey(mediaKey)
    if (!normalizedMediaKey || typeof listener !== 'function') {
        return () => {}
    }

    let listeners = listenersByMediaKey.get(normalizedMediaKey)
    if (!listeners) {
        listeners = new Set()
        listenersByMediaKey.set(normalizedMediaKey, listeners)
    }

    listeners.add(listener)

    return () => {
        const currentListeners = listenersByMediaKey.get(normalizedMediaKey)
        if (!currentListeners) return

        currentListeners.delete(listener)
        if (!currentListeners.size) {
            listenersByMediaKey.delete(normalizedMediaKey)
        }
    }
}
